import { registerLocaleVariant } from "../../registry";
import { makeId } from "../../helpers";
import type { TestBody } from "../../types";

// NOTE: costRange values are US dollar amounts inherited from the EN blocks.
// In France, most of these tests are covered by Sécurité sociale within an
// AMP parcours — the $ display is misleading. Flagged for a UI follow-up
// (locale-aware cost rendering in TestDetailSheet).

// ─── Helper ─────────────────────────────────────────────────────────

function frTest(
  slug: string,
  variant: {
    title: string;
    subtitle: string;
    body: Partial<TestBody>;
  },
): void {
  registerLocaleVariant(makeId(slug), "fr", variant);
}

// ─── Fragments partagés — biopsies endométriales ─────────────────────

const BIOPSY_QUICK_FACTS: NonNullable<TestBody["quickFacts"]> = [
  { kind: "sample", value: "Biopsie de tissu endométrial" },
  { kind: "time", value: "Biopsie de 5 à 10 min" },
  { kind: "pain", value: "Crampes pendant et juste après" },
  { kind: "anesthesia", value: "En général aucune" },
  {
    kind: "prep",
    value: "Pas de jeûne en général ; demander pour l'ibuprofène",
  },
  { kind: "results", value: "2 à 3 semaines" },
];

const RECEPTIVITY_RESULT_TYPES: NonNullable<TestBody["resultTypes"]> = [
  {
    label: "Réceptif",
    meaning:
      "Le timing de la biopsie correspond à la fenêtre d'implantation prédite par le laboratoire.",
    whatToExpect:
      "Les cliniques gardent en général le timing standard. Ça ne garantit pas l'implantation et ne prouve pas que le test a amélioré tes chances.",
  },
  {
    label: "Pré-réceptif",
    meaning:
      "Le laboratoire prédit que la biopsie a été prise avant que l'endomètre atteigne sa fenêtre réceptive.",
    whatToExpect:
      "Le rapport peut suggérer plus d'exposition à la progestérone avant transfert. Les grands essais n'ont pas montré de bénéfice de routine sur les naissances vivantes.",
  },
  {
    label: "Post-réceptif",
    meaning:
      "Le laboratoire prédit que la biopsie a été prise après la fenêtre réceptive.",
    whatToExpect:
      "Le rapport peut suggérer moins de progestérone ou un transfert décalé. Demande précisément comment le plan changerait et pourquoi.",
  },
  {
    label: "Peu concluant",
    meaning:
      "L'échantillon ou le timing ne permet pas une interprétation fiable.",
    whatToExpect:
      "Une nouvelle biopsie peut être proposée — coût, inconfort et délai supplémentaires, sans bénéfice de routine prouvé.",
  },
];

const RECEPTIVITY_BEST_FOR: NonNullable<TestBody["bestFor"]> = [
  "Échec d'implantation répété — usage débattu",
  "Non recommandé en routine",
];

const NO_LIVE_BIRTH_HEADLINE = "Pas de bénéfice sur la naissance vivante";

const MICROBIOME_RESULT_TYPES: NonNullable<TestBody["resultTypes"]> = [
  {
    label: "À dominance Lactobacillus",
    meaning:
      "Le rapport décrit un microbiome principalement composé d'espèces de Lactobacillus.",
    whatToExpect:
      "C'est en général vu comme un profil rassurant, mais ça ne prouve pas que l'implantation aura lieu ni que le test améliore les naissances vivantes.",
  },
  {
    label: "Dysbiotique",
    meaning:
      "Le rapport montre moins de Lactobacillus et plus d'autres bactéries que ce qui est attendu.",
    whatToExpect:
      "Le laboratoire ou la clinique peut suggérer probiotiques ou antibiotiques. Demande quel micro-organisme, seuil et preuve justifient un traitement avant de décaler un transfert.",
  },
  {
    label: "Bactéries pathogènes détectées",
    meaning:
      "Des bactéries associées à une infection ou à l'endométrite chronique peuvent être détectées.",
    whatToExpect:
      "L'interprétation rejoint celle d'ALICE. Demande si l'endométrite chronique est réellement suspectée et comment vérifier le succès du traitement.",
  },
  {
    label: "Faible biomasse ou peu concluant",
    meaning:
      "L'ADN bactérien peut être trop faible ou le résultat trop incertain pour être interprété.",
    whatToExpect:
      "La clinique peut proposer une nouvelle biopsie ou ne rien faire. Évite de traiter un résultat bruité comme un diagnostic clair.",
  },
];

const MICROBIOME_BEST_FOR: NonNullable<TestBody["bestFor"]> = [
  "Contexte de recherche",
  "Cas sélectionnés d'échecs d'implantation répétés après bilan standard",
  "Non recommandé comme add-on de routine en FIV",
];

const RECEPTIVITY_KEY_FINDINGS: NonNullable<TestBody["keyFindings"]> = [
  {
    direction: "negative",
    outcome:
      "Un essai randomisé n'a pas trouvé d'amélioration des naissances vivantes avec un transfert calé sur la réceptivité",
    magnitude:
      "Naissances vivantes : 58,5 % (réceptivité) vs 61,9 % (standard)",
    source: "Doyle et al., JAMA 2022 (RCT, n=767)",
  },
  {
    direction: "negative",
    outcome:
      "La HFEA classe les tests de réceptivité endométriale « rouge » pour la majorité des patientes",
    source: "HFEA, guide add-ons, 2023",
  },
];

const MICROBIOME_KEY_FINDINGS: NonNullable<TestBody["keyFindings"]> = [
  {
    direction: "negative",
    outcome:
      "Le NICE n'a trouvé aucune preuve d'efficacité pour les tests microbiologiques comme EMMA ou ALICE",
    source: "NICE NG257, revue de preuves, 2026",
  },
  {
    direction: "negative",
    outcome:
      "L'ESHRE 2023 (add-ons) et la HFEA ne recommandent pas le test du microbiome en routine",
    source: "Recommandations ESHRE add-ons, Human Reproduction 2023",
  },
];

const MICROBIOME_EXPECTATIONS: NonNullable<TestBody["expectations"]> = {
  canExpect: [
    "Une biopsie de l'endomètre avec séquençage de l'ADN bactérien",
    "Un rapport décrivant le pourcentage de Lactobacillus et les autres bactéries détectées",
    "Éventuellement des suggestions d'antibiotiques ou de probiotiques",
  ],
  shouldNotExpect: [
    "Tester et traiter le microbiome n'a pas prouvé qu'il améliorait les naissances vivantes",
    "Un usage de routine peut décaler le transfert sans indication précise",
    "Les échantillons à faible biomasse peuvent être bruités",
  ],
};

const RECEPTIVITY_EXPECTATIONS: NonNullable<TestBody["expectations"]> = {
  canExpect: [
    "Un rapport de réceptivité classant l'échantillon en réceptif, pré-réceptif ou post-réceptif",
    "Un résultat à interpréter dans le contexte plus large des preuves sur la réceptivité",
  ],
  shouldNotExpect: [
    "Ne pas attendre du résultat qu'il garantisse l'implantation",
    "Ne pas attendre qu'il explique tout transfert raté",
    "Ne remplace pas la qualité embryonnaire, l'évaluation utérine ou le contexte clinique",
  ],
};

const CHRONIC_ENDO_STEPS: NonNullable<TestBody["howItWorksSteps"]> = [
  "Un praticien réalise une biopsie de l'endomètre avec un fin cathéter, généralement au cours d'une hystéroscopie.",
  "Le laboratoire d'anatomopathologie recherche l'endométrite chronique selon la méthode prescrite : histologie, immunomarquage CD138, hystéroscopie, culture, PCR, NGS ou panel commercial comme ALICE.",
  "Le timing dépend du fait que le bilan d'endométrite chronique soit isolé ou groupé avec un test de réceptivité ; confirme le timing du cycle simulé avec ta clinique.",
  "Le résultat peut mener à un traitement antibiotique ou probiotique avant transfert, mais ne remplace pas une évaluation clinique menée par ton équipe.",
];

const RECEPTIVITY_STEPS: NonNullable<TestBody["howItWorksSteps"]> = [
  "Ta clinique programme le test dans un cycle simulé naturel ou médicamenté de TEC, pas dans le cycle de transfert réel.",
  "La biopsie est calée sur la fenêtre d'implantation attendue : souvent P+5 en cycle médicamenté ou LH/hCG+7 en cycle naturel.",
  "Un praticien prélève une biopsie endométriale brève avec un fin cathéter.",
  "Le laboratoire analyse l'expression génique et classe l'échantillon comme réceptif, pré-réceptif, post-réceptif ou peu concluant.",
  "Si le rapport indique un timing décalé, la clinique peut proposer d'ajuster l'exposition à la progestérone ou le timing du transfert au cycle suivant.",
];

function frDataPoint(
  slug: string,
  variant: {
    title: string;
    body: {
      value?: string;
      unit?: string;
      normalRange?: string;
      interpretation?: string;
    };
  },
): void {
  registerLocaleVariant(makeId(slug), "fr", variant);
}

// ─── AMH ────────────────────────────────────────────────────────────

frTest("test-amh", {
  title: "AMH",
  subtitle: "Kits à domicile par piqûre au doigt, ou n'importe quel labo",
  body: {
    testName: "Hormone anti-müllérienne (AMH)",
    whyItMatters: [
      "L'AMH sert de repère de réserve ovarienne pour la planification, pas de mesure directe de la qualité ovocytaire.",
      "L'AMH décline en général avec l'âge. C'est plus utile comme repère de projet que comme cible à optimiser à court terme.",
      "Si l'AMH est suivie dans le temps, beaucoup de cliniques la répètent tous les 6 mois environ plutôt qu'après chaque intervention.",
    ],
    whatItMeasures:
      "Donne une estimation du stock d'ovocytes restant (réserve ovarienne). Plus haute = plus d'ovocytes. Ne mesure PAS la qualité ovocytaire.",
    howItWorks:
      "Simple prise de sang par kit à domicile ou dans n'importe quel laboratoire. L'AMH peut être faite n'importe quel jour du cycle car elle varie moins que la FSH ou la LH. À jeun généralement non requis.",
    turnaroundTime: "2 à 5 jours ouvrés",
    cycleDay:
      "N'importe quel jour du cycle. Si l'AMH est groupée avec la FSH ou l'œstradiol, le rendez-vous peut être au jour 2 à 4 pour ces autres marqueurs.",
    quickFacts: [
      { kind: "sample", value: "Sang ou piqûre au doigt" },
      { kind: "time", value: "5 min de prélèvement ou kit à domicile" },
      { kind: "pain", value: "Piqûre d'aiguille ou de lancette" },
      {
        kind: "prep",
        value: "N'importe quel jour du cycle ; jeûne en général non requis",
      },
      { kind: "results", value: "2 à 5 jours ouvrés" },
    ],
    markerTiming: [
      {
        marker: "AMH",
        timing: "N'importe quel jour du cycle",
        reason:
          "L'AMH est produite par les petits follicules en croissance et varie moins au cours du cycle que la FSH, la LH ou l'œstradiol.",
      },
    ],
  },
});

frDataPoint("dp-amh-level", {
  title: "Taux d'AMH",
  body: {
    normalRange: "1,0 à 3,5 ng/mL (varie selon l'âge)",
    interpretation:
      "L'AMH décline avec l'âge. En dessous de 1,0 suggère une réserve diminuée. Au-dessus de 3,5 peut suggérer un PMOS (ex SOPK). Le contexte compte — ta médecin l'interprète avec le CFA et l'âge.",
  },
});

// ─── AFC ────────────────────────────────────────────────────────────

frTest("test-afc", {
  title: "Comptage des follicules antraux (CFA)",
  subtitle:
    "Comptage échographique des follicules au repos — marqueur clé de la réserve ovarienne",
  body: {
    testName: "Comptage des follicules antraux (CFA)",
    whyItMatters: [
      "Le CFA est utile comme marqueur de base de réserve ovarienne et pour caler la dose de stimulation.",
      "Il peut varier d'environ 2 à 5 follicules d'un cycle à l'autre — un comptage isolé se lit avec l'AMH, l'âge et l'ensemble du contexte clinique.",
      "Ce n'est pas un marqueur à optimiser à court terme comme la vitamine D, la TSH ou les marqueurs métaboliques.",
    ],
    quickFacts: [
      { kind: "sample", value: "Échographie endovaginale" },
      { kind: "time", value: "5 à 10 min d'examen" },
      { kind: "pain", value: "Pression, généralement modérée" },
      { kind: "prep", value: "Jour 2 à 4 du cycle" },
      { kind: "results", value: "Pendant le rendez-vous" },
    ],
    whatItMeasures:
      "Compte les petits follicules au repos (2 à 10 mm) visibles sur chaque ovaire à l'échographie. Avec l'AMH, c'est l'un des deux marqueurs les plus importants de la réserve ovarienne. Un comptage plus élevé suggère plus d'ovocytes disponibles pour la stimulation.",
    howItWorks:
      "Échographie endovaginale réalisée au jour 2 à 4 du cycle. L'échographiste compte tous les follicules antraux visibles sur les deux ovaires. Chaque follicule contient potentiellement un ovocyte qui pourrait répondre aux médicaments de stimulation. Le comptage aide à prédire combien d'ovocytes pourraient être recueillis en FIV et oriente le dosage des médicaments.",
    turnaroundTime: "Immédiat — résultats pendant le rendez-vous d'échographie",
    cycleDay: "Jour 2 à 4",
  },
});

// ─── Baseline ultrasound ────────────────────────────────────────────

frTest("test-baseline-ultrasound", {
  title: "Échographie de référence",
  subtitle: "Échographie de début de cycle pour confirmer que tu peux démarrer",
  body: {
    testName: "Échographie de référence et bilan sanguin",
    quickFacts: [
      { kind: "sample", value: "Échographie et prise de sang" },
      { kind: "time", value: "15 à 30 min de rendez-vous" },
      { kind: "pain", value: "Pression modérée + piqûre d'aiguille" },
      { kind: "prep", value: "Appeler au jour 1 ; échographie au jour 2 à 4" },
      { kind: "results", value: "En général le jour même" },
    ],
    whatItMeasures:
      "Confirme qu'il n'y a pas de kystes, établit ton comptage de follicules antraux (CFA), et vérifie que les niveaux hormonaux sont au repos avant de commencer la stimulation ou un cycle médicamenté.",
    howItWorks:
      "Au jour 2 à 4 du cycle, le plus souvent jour 2 ou 3, tu viens pour une échographie endovaginale. L'échographiste compte les petits follicules antraux au repos sur chaque ovaire. Ce CFA aide à prédire la réponse ovarienne aux médicaments. L'endomètre est aussi mesuré pour vérifier qu'il a bien desquamé. Une prise de sang est faite au même rendez-vous pour vérifier l'œstradiol et parfois la FSH ou la LH. Si tout est clair, sans kyste résiduel et avec des hormones au repos, ta clinique peut généralement te donner le feu vert pour commencer les médicaments le jour même ou le lendemain. Appelle ta clinique au jour 1 pour réserver le rendez-vous. Continue les médicaments pré-cycle comme indiqué.",
    cycleDay: "Jour 2 à 4, le plus souvent jour 2 ou 3",
    markerTiming: [
      {
        marker: "CFA et endomètre",
        timing: "Jour 2 à 4, le plus souvent jour 2 ou 3",
        reason:
          "Le début de cycle permet de compter les follicules au repos avant qu'un follicule dominant ne grossisse, et de vérifier que l'endomètre est fin après les règles.",
      },
      {
        marker: "Œstradiol, FSH ou LH si prélevés",
        timing: "Jour 2 à 4 en valeur de base",
        reason:
          "Le bilan sanguin de début de cycle vérifie que les hormones sont assez au repos pour démarrer la stimulation ou un cycle médicamenté.",
      },
    ],
  },
});

// ─── Beta hCG ───────────────────────────────────────────────────────

frTest("test-beta-hcg", {
  title: "Bêta-hCG",
  subtitle: "Test de grossesse sanguin pour détecter les taux d'hCG",
  body: {
    testName: "Béta-hCG (prise de sang de grossesse)",
    quickFacts: [
      { kind: "sample", value: "Sang" },
      { kind: "time", value: "5 min de prélèvement" },
      { kind: "pain", value: "Piqûre d'aiguille" },
      {
        kind: "prep",
        value: "Suis les consignes de médicaments de ta clinique",
      },
      { kind: "results", value: "Jour même, souvent en quelques heures" },
    ],
    whatItMeasures:
      "Mesure l'hormone chorionique gonadotrope (hCG), produite par les tissus de la grossesse. Le résultat de la bêta-hCG s'interprète avec le moment du traitement, les symptômes, les dosages répétés et, si nécessaire, l'échographie ; un seul résultat ne permet pas à lui seul d'établir la localisation ou l'évolution de la grossesse.",
    howItWorks:
      "Un petit échantillon de sang est prélevé dans une veine et analysé par le laboratoire. Votre centre fixe la date du dosage et peut demander des contrôles répétés selon le protocole et le premier résultat. Un seuil ou une vitesse d'augmentation ne constitue pas un diagnostic à lui seul : les dosages répétés, l'échographie, les symptômes et l'examen peuvent tous compter. N'utilisez pas le résultat pour arrêter ou modifier un médicament ; suivez le plan de votre équipe et demandez un avis urgent en cas de douleur importante, malaise, douleur à l'épaule ou saignement abondant.",
    turnaroundTime: "Jour même (quelques heures)",
    cycleDay:
      "Calendrier fixé par le protocole de votre centre après le traitement",
    markerTiming: [
      {
        marker: "Béta-hCG",
        timing:
          "Calendrier et éventuel contrôle fixés par le protocole de votre centre et le résultat",
        reason:
          "Le moment et l'interprétation de l'hCG dépendent de la date du traitement, de la méthode d'analyse, des contrôles, des symptômes et de l'évaluation clinique.",
      },
    ],
  },
});

// ─── Carrier screening ─────────────────────────────────────────────

frTest("test-carrier-screening", {
  title: "Dépistage génétique de porteur étendu",
  subtitle:
    "Rapport ciblé de statut de porteur pouvant être généré à partir du séquençage du génome entier",
  body: {
    testName: "Dépistage génétique de porteur étendu",
    whatItMeasures:
      "Dépistage de plus de 200 conditions génétiques récessives (mucoviscidose, SMA, X fragile, drépanocytose, Tay-Sachs, etc.). Ce rapport est inclus dans le séquençage du génome entier, qui peut fournir les mêmes informations de portage avec des données diagnostiques, reproductives et pharmacogénomiques plus larges.",
    howItWorks:
      "Prise de sang ou prélèvement salivaire. Louise peut techniquement produire ce rapport à partir d'un séquençage du génome entier. Les deux partenaires devraient être testés. Si les deux portent la même condition, un conseil génétique peut revoir la conception naturelle, le diagnostic prénatal, la FIV avec PGT-M ou les options de don. En France, la HAS ne recommande pas le dépistage de porteur étendu panéthnique en routine pour la population générale : il est proposé sur indication (antécédents familiaux, origine, statut du partenaire), pas systématiquement.",
    turnaroundTime: "2 à 3 semaines",
    quickFacts: [
      { kind: "sample", value: "Sang ou salive" },
      { kind: "time", value: "5 min de prélèvement ou kit salivaire" },
      { kind: "pain", value: "Piqûre d'aiguille si sang" },
      { kind: "prep", value: "Recommandé pour les deux partenaires" },
      { kind: "results", value: "2 à 3 semaines" },
    ],
    resultTypes: [
      {
        label: "Aucun risque augmenté détecté",
        meaning:
          "Aucun variant à risque significatif n'a été détecté sur les conditions testées.",
        whatToExpect:
          "C'est rassurant, mais ça ne couvre pas toutes les conditions génétiques possibles et le risque résiduel dépend du panel utilisé.",
      },
      {
        label: "Porteur d'une condition",
        meaning:
          "Le rapport détecte que tu portes un variant récessif ou lié à l'X pour une condition testée.",
        whatToExpect:
          "L'autre partenaire est testé pour la même condition ; un conseil génétique aide à discuter conception naturelle, DPI (PGT-M), don ou diagnostic prénatal si les deux sont porteurs.",
      },
      {
        label: "Les deux partenaires porteurs de la même condition",
        meaning:
          "Chaque partenaire porte un variant sur le même gène récessif.",
        whatToExpect:
          "Un conseil génétique passe en revue les options : conception naturelle avec DPN, FIV avec PGT-M, don de gamètes ou d'embryon.",
      },
    ],
  },
});

// ─── CBC ─────────────────────────────────────────────────────────────

frTest("test-cbc", {
  title: "NFS",
  subtitle:
    "Dépistage de l'anémie et de l'infection avant traitement de fertilité",
  body: {
    testName: "Numération formule sanguine (NFS)",
    whatItMeasures:
      "Santé sanguine globale — globules rouges, globules blancs, plaquettes, hémoglobine. Dépistage de l'anémie et de l'infection avant traitement de fertilité.",
    howItWorks:
      "Prise de sang de routine, n'importe quel labo. Pas besoin d'être à jeun. Fait partie du bilan pré-cycle standard.",
    turnaroundTime: "Jour même à 1 jour ouvré",
    quickFacts: [
      { kind: "sample", value: "Sang" },
      { kind: "time", value: "5 min de prélèvement" },
      { kind: "pain", value: "Piqûre d'aiguille" },
      { kind: "prep", value: "Pas de jeûne en général" },
      { kind: "results", value: "Jour même à 1 jour ouvré" },
    ],
  },
});

// ─── Endometrial microbiome / chronic endometritis ──────────────────

frTest("test-endometrial-microbiome-testing", {
  title: "Test du microbiome endométrial (EMMA)",
  subtitle:
    "Investigationnel - hors recommandations CNGOF, non remboursé en France",
  body: {
    testName: "Test du microbiome endométrial",
    soldAs: [
      {
        name: "EMMA",
        company: "Igenomix",
        description:
          "Test moléculaire du microbiome endométrial : séquence l'ADN bactérien à partir d'une biopsie endométriale.",
        evidenceSummary:
          "Peut décrire le profil du microbiome, mais aucune preuve produit-spécifique de bénéfice sur les naissances vivantes en usage routinier.",
      },
      {
        name: "ERBiome",
        company: "CooperSurgical",
        description:
          "Test moléculaire du microbiome endométrial pouvant être commandé seul ou dans le bundle ER-Complete.",
        evidenceSummary:
          "Même catégorie de test que EMMA : les recommandations indépendantes ne trouvent pas de preuves d'efficacité pour un usage de routine en fertilité.",
      },
    ],
    whatItMeasures:
      "Cartographie l'ADN bactérien trouvé dans une biopsie endométriale, dont les lactobacilles. EMMA est la version Igenomix (échantillon envoyé en Espagne, ~595 €). ⚠️ Aucune position favorable du CNGOF, de la HAS ou de l'Agence de la biomédecine. L'ESHRE 2023 (recommandations sur les add-ons) ne recommande pas son utilisation en routine. Ce n'est pas un examen standard de la PMA française - il reste considéré comme investigationnel.",
    howItWorks:
      "Une biopsie endométriale est réalisée en France, puis envoyée au laboratoire Igenomix en Espagne pour séquençage. Le rapport décrit la composition du microbiome et peut suggérer antibiotiques ou probiotiques. Détecter des bactéries ne prouve pas que les traiter améliore les naissances vivantes. Non remboursé par l'Assurance Maladie, strictement à la charge de la patiente.",
    turnaroundTime: "2 à 3 semaines",
    cycleDay:
      "Le timing dépend du fait qu'EMMA soit fait seul ou groupé avec ERA; confirme le timing du cycle simulé avec la clinique.",
    quickFacts: [
      { kind: "sample", value: "Biopsie de tissu endométrial" },
      { kind: "time", value: "Biopsie de 5 à 10 min" },
      { kind: "pain", value: "Crampes pendant et juste après" },
      { kind: "anesthesia", value: "En général aucune" },
      {
        kind: "prep",
        value: "Pas de jeûne en général ; demander pour l'ibuprofène",
      },
      { kind: "results", value: "2 à 3 semaines" },
    ],
    resultTypes: [
      {
        label: "À dominance Lactobacillus",
        meaning:
          "Le rapport décrit un microbiome principalement composé d'espèces de Lactobacillus.",
        whatToExpect:
          "C'est en général vu comme un profil rassurant, mais ça ne prouve pas que l'implantation aura lieu ni que le test améliore les naissances vivantes.",
      },
      {
        label: "Dysbiotique",
        meaning:
          "Le rapport montre moins de Lactobacillus et plus d'autres bactéries que ce qui est attendu.",
        whatToExpect:
          "Le laboratoire ou la clinique peut suggérer probiotiques ou antibiotiques. Demande quel micro-organisme précis, quel seuil et quelle preuve justifient un traitement avant de décaler un transfert.",
      },
      {
        label: "Bactéries pathogènes détectées",
        meaning:
          "Des bactéries associées à une infection ou à l'endométrite chronique peuvent être détectées.",
        whatToExpect:
          "L'interprétation rejoint celle d'ALICE. Demande si l'endométrite chronique est réellement suspectée et comment le succès du traitement serait vérifié.",
      },
      {
        label: "Faible biomasse ou peu concluant",
        meaning:
          "L'ADN bactérien peut être trop faible ou le résultat trop incertain pour être interprété avec confiance.",
        whatToExpect:
          "La clinique peut proposer de refaire la biopsie ou de ne rien faire. Évite de traiter un échantillon bruité ou peu concluant comme un diagnostic clair.",
      },
    ],
    expectations: {
      canExpect: [
        "Une biopsie de l'endomètre avec séquençage de l'ADN bactérien",
        "Un rapport décrivant le pourcentage de Lactobacillus et les autres bactéries détectées",
        "Éventuellement des suggestions d'antibiotiques ou de probiotiques par le laboratoire ou le clinicien",
      ],
      shouldNotExpect: [
        "Tester et traiter le microbiome n'a pas prouvé qu'il améliorait les naissances vivantes",
        "Un usage de routine peut décaler le transfert sans indication précise",
        "Les échantillons à faible biomasse peuvent être bruités et mal distinguer infection et contamination",
      ],
    },
    considerations: [
      "Le NICE n'a trouvé aucune preuve d'efficacité pour les tests microbiologiques comme EMMA ou ALICE en add-on de fertilité",
      "Une biopsie ajoute inconfort, saignements, risque infectieux, coût et souvent un délai de cycle",
      "Si l'endométrite chronique est suspectée, demande comment le diagnostic sera confirmé et comment le succès du traitement sera vérifié",
      "Ne pas traiter cet examen comme un dépistage de routine avant transfert sans indication clinique précise",
      "Demande si ta clinique prescrit EMMA, ERBiome ou un autre panel microbiome développé en laboratoire",
    ],
    bestFor: [
      "Contexte de recherche",
      "Certains cas d'échecs d'implantation répétés après bilan standard",
      "Non recommandé comme add-on de routine en FIV",
    ],
    keyFindings: [
      {
        direction: "negative",
        outcome:
          "Le NICE n'a trouvé aucune preuve d'efficacité pour les tests microbiologiques comme EMMA ou ALICE",
        mechanism:
          "Le test peut détecter des bactéries, mais la détection ne prouve pas que traiter le résultat améliore les naissances vivantes",
        source: "NICE NG257, revue de preuves, 2026",
      },
      {
        direction: "neutral",
        outcome:
          "Des études observationnelles suggèrent une association entre profils du microbiome endométrial et résultats, sans prouver que le test change les résultats",
        source: "Moreno et al., Microbiome 2022 (association observationnelle)",
      },
      {
        direction: "negative",
        outcome:
          "L'ESHRE 2023 (add-ons) et la HFEA ne recommandent pas le test du microbiome en routine — pas d'essai randomisé disponible",
        source: "Recommandations ESHRE add-ons, Human Reproduction 2023",
      },
    ],
    headlineStat: "Pas de bénéfice prouvé sur la naissance vivante",
  },
});

frTest("test-chronic-endometritis-testing", {
  title: "Endométrite chronique : hystéroscopie + biopsie + CD138",
  subtitle:
    "Standard français : immunomarquage CD138 sur biopsie hystéroscopique - remboursé",
  body: {
    testName: "Endométrite chronique - bilan français standard",
    howItWorksSteps: CHRONIC_ENDO_STEPS,
    soldAs: [
      {
        name: "ALICE",
        company: "Igenomix",
        description:
          "Test moléculaire de recherche de bactéries associées à l'endométrite chronique sur biopsie endométriale.",
        evidenceSummary:
          "Peut rapporter des micro-organismes détectés, mais les recommandations indépendantes ne trouvent pas de preuves qu'un test-and-treat commercial de routine améliore les résultats.",
      },
    ],
    whatItMeasures:
      "Recherche d'une endométrite chronique (infection/inflammation persistante de l'endomètre) souvent suspectée en cas d'échecs d'implantation répétés ou de fausses couches récurrentes. En France, le standard est l'hystéroscopie diagnostique + biopsie d'endomètre + immunomarquage CD138 (syndécane-1) à la recherche de plasmocytes - examen reconnu et remboursé par l'Assurance Maladie quand prescrit pour RIF ou FCSR. Le test ALICE (panel moléculaire Igenomix, envoyé en Espagne, ~395 €) est une alternative privée non remboursée et non standard.",
    howItWorks:
      "Hystéroscopie réalisée en cabinet ou ambulatoire, avec biopsie d'endomètre. L'échantillon est analysé en anatomopathologie (Cerba, Eurofins Biomnis, services hospitaliers) avec un marquage CD138 à la recherche de plasmocytes. Le diagnostic est posé histologiquement. Un traitement antibiotique peut être proposé en cas de résultat positif. Examen non recommandé en dépistage de routine pour toutes les patientes IVF - réservé aux indications cliniques (RIF, FCSR).",
    turnaroundTime: "2 à 3 semaines pour l'histologie",
    cycleDay:
      "Phase folliculaire (jours 6 à 11 du cycle) - éviter la phase lutéale et les saignements.",
    quickFacts: [
      { kind: "sample", value: "Biopsie de tissu endométrial" },
      { kind: "time", value: "Biopsie de 5 à 10 min" },
      { kind: "pain", value: "Crampes pendant et juste après" },
      { kind: "anesthesia", value: "En général aucune" },
      {
        kind: "prep",
        value: "Pas de jeûne en général ; demander pour l'ibuprofène",
      },
      { kind: "results", value: "2 à 3 semaines" },
    ],
    resultTypes: [
      {
        label: "Non détectée",
        meaning:
          "Le rapport ne détecte pas les bactéries associées à l'endométrite chronique incluses dans le panel.",
        whatToExpect:
          "C'est rassurant pour les micro-organismes testés, mais ça n'exclut ni tous les facteurs utérins, ni toutes les infections, ni les causes non infectieuses d'échec d'implantation.",
      },
      {
        label: "Détectée",
        meaning:
          "Le rapport détecte une ou plusieurs bactéries associées à l'endométrite chronique.",
        whatToExpect:
          "La clinique peut discuter antibiotiques, probiotiques ou examens complémentaires. Un organisme détecté n'est pas automatiquement actionnable sans contexte clinique.",
      },
      {
        label: "Peu concluant",
        meaning:
          "L'échantillon ou le séquençage n'est pas assez clair pour trancher avec confiance.",
        whatToExpect:
          "La clinique peut proposer de refaire la biopsie ou d'utiliser un autre bilan (histologie, CD138, hystéroscopie, culture ou PCR).",
      },
    ],
    expectations: {
      canExpect: [
        "Un rapport basé sur biopsie centré sur l'endométrite chronique, l'inflammation ou les bactéries associées",
        "Un traitement antibiotique possible si des bactéries sont détectées",
        "Un examen souvent proposé après échecs d'implantation répétés, fausses couches à répétition ou symptômes évoquant une inflammation utérine",
      ],
      shouldNotExpect: [
        "Un dépistage de routine de l'endométrite chronique par panels moléculaires n'a pas montré d'amélioration des naissances vivantes pour la majorité des patientes en FIV",
        "Ce test ne remplace pas un bilan clinique conduit par ton équipe",
        "Un organisme détecté n'est pas automatiquement actionnable sans contexte clinique",
      ],
    },
    considerations: [
      "Demande si l'endométrite chronique est cliniquement suspectée ou si le test est proposé comme add-on générique",
      "Demande quelle méthode est utilisée : histologie, CD138, hystéroscopie, culture, PCR, NGS ou panel commercial",
      "Les antibiotiques ont des effets secondaires et peuvent perturber le microbiome — un traitement doit avoir une raison précise",
      "Un résultat négatif ne dit pas que tout facteur utérin d'implantation est écarté",
    ],
    bestFor: [
      "Suspicion clinique d'endométrite chronique avec suivi médical",
      "Bilan d'échecs d'implantation répétés ou fausses couches à répétition, en cas d'indication",
      "Non recommandé comme add-on de routine en FIV",
    ],
    keyFindings: [
      {
        direction: "negative",
        outcome:
          "Le NICE n'a trouvé aucune preuve éligible que les tests microbiologiques comme ALICE améliorent les résultats de la fertilité",
        mechanism:
          "Détecter des bactéries associées à l'endométrite n'équivaut pas à prouver qu'un test-and-treat commercial améliore les naissances vivantes",
        source: "NICE NG257, revue de preuves, 2026",
      },
      {
        direction: "neutral",
        outcome:
          "L'endométrite chronique peut compter cliniquement, mais la meilleure stratégie diagnostique et le bénéfice fertilité des panels moléculaires commerciaux restent débattus",
        source:
          "ESHRE, recommandations sur l'échec d'implantation répété, 2023",
      },
      {
        direction: "negative",
        outcome:
          "Un test de routine ajoute risque de biopsie, antibiotiques, coût et délai de transfert sans bénéfice prouvé",
        source: "NICE NG257, revue de preuves, 2026",
      },
    ],
    headlineStat: "Pas de bénéfice prouvé par essai randomisé",
  },
});

// ─── EndomeTRIO ─────────────────────────────────────────────────────

frTest("test-endometrial-add-on-bundle", {
  title: "Bundle EndomeTRIO (ERA + EMMA + ALICE)",
  subtitle:
    "Hors recommandations CNGOF, non remboursé - ~995 € à la charge de la patiente",
  body: {
    testName: "Bundle d'add-ons endométriaux",
    soldAs: [
      {
        name: "EndomeTRIO",
        company: "Igenomix",
        description:
          "Bundle qui combine ERA, EMMA et ALICE à partir d'une seule biopsie endométriale.",
        evidenceSummary:
          "Regrouper les tests ajoute des résultats, mais ne résout pas les lacunes de preuve sur la réceptivité et le microbiome.",
      },
      {
        name: "ER-Complete",
        company: "CooperSurgical",
        description:
          "Bundle qui combine le test de réceptivité ERPeak et le test du microbiome ERBiome.",
        evidenceSummary:
          "À interpréter à travers les lacunes de preuve à la fois sur la réceptivité et le microbiome.",
      },
    ],
    whatItMeasures:
      "Combine le test de réceptivité (ERA), l'analyse du microbiome (EMMA) et le test moléculaire d'endométrite chronique (ALICE) sur une seule biopsie. EndomeTRIO est le bundle commercial Igenomix (~995 €, échantillon envoyé en Espagne). ⚠️ Aucune des trois composantes n'est dans les recommandations CNGOF ou HAS pour la PMA standard. L'ESHRE 2023 ne recommande pas leur usage en routine. Bundle produit plus de données, mais sans preuves d'amélioration des naissances vivantes - non remboursé.",
    howItWorks:
      "Une biopsie endométriale est réalisée en France dans un cycle simulé de transfert, puis envoyée chez Igenomix en Espagne. Le rapport peut suggérer de décaler le transfert, d'utiliser antibiotiques ou probiotiques, ou de refaire une biopsie. Réservé à des situations privées spécifiques (échecs d'implantation répétés non expliqués) - pas un examen de PMA standard en France.",
    turnaroundTime: "2 à 3 semaines",
    cycleDay:
      "Généralement calé comme ERA dans un cycle simulé: P+5 en cycle médicamenté ou LH/hCG+7 en cycle naturel.",
    quickFacts: [
      { kind: "sample", value: "Biopsie de tissu endométrial" },
      { kind: "time", value: "Biopsie de 5 à 10 min" },
      { kind: "pain", value: "Crampes pendant et juste après" },
      { kind: "anesthesia", value: "En général aucune" },
      {
        kind: "prep",
        value: "Pas de jeûne en général ; demander pour l'ibuprofène",
      },
      { kind: "results", value: "2 à 3 semaines" },
    ],
    resultTypes: [
      {
        label: "Aucune composante signalée",
        meaning:
          "La réceptivité est réceptive, le microbiome n'est pas signalé et l'endométrite chronique ne détecte rien d'inquiétant.",
        whatToExpect:
          "Ça peut rassurer, mais ça ne garantit pas l'implantation ni ne prouve que le bundle améliore les naissances vivantes.",
      },
      {
        label: "ERA non-réceptif",
        meaning:
          "La partie réceptivité classe l'échantillon comme pré-réceptif ou post-réceptif.",
        whatToExpect:
          "Le rapport peut suggérer un ajustement de la progestérone ou du timing du transfert. Les essais randomisés n'ont pas montré de bénéfice de routine sur les naissances vivantes.",
      },
      {
        label: "Microbiome ou inflammation signalé",
        meaning: "La partie microbiome ou endométrite chronique est signalée.",
        whatToExpect:
          "Antibiotiques, probiotiques ou nouvelle biopsie peuvent être proposés. Demande quelle composante motive le plan et si ce changement repose sur des preuves.",
      },
      {
        label: "Mixte ou peu concluant",
        meaning:
          "Une ou plusieurs composantes sont floues, à faible biomasse ou incohérentes avec le reste.",
        whatToExpect:
          "Peut mener à une nouvelle biopsie ou à un délai. Demande ce qui se passerait si tu ignorais la composante floue et continuais un parcours standard.",
      },
    ],
    expectations: {
      canExpect: [
        "Une seule biopsie utilisée pour la réceptivité, le microbiome et la recherche de bactéries d'endométrite chronique",
        "Un rapport groupé pouvant suggérer un changement de timing, antibiotiques, probiotiques ou nouvelle biopsie",
        "Un délai avant transfert, car la biopsie est en général faite en cycle simulé",
      ],
      shouldNotExpect: [
        "Regrouper réceptivité, microbiome et endométrite chronique ne crée pas de preuves plus solides",
        "Les recommandations indépendantes ne préconisent pas un usage de routine",
        "Un rapport anormal ne prouve pas qu'il explique l'échec d'implantation",
      ],
    },
    considerations: [
      "Demande quelle composante est recommandée et pourquoi : réceptivité, microbiome, endométrite chronique ou les trois",
      "Demande si un bilan utérin plus simple et mieux étayé a déjà été fait",
      "Demande comment ton plan change si une composante est anormale, et si ce changement repose sur des preuves",
      "Évite le bundle si la prescription est vague ou de routine",
    ],
    bestFor: [
      "Contexte de recherche",
      "Cas très sélectionnés d'échecs d'implantation répétés après bilan standard",
      "Non recommandé comme add-on de routine en FIV",
    ],
    keyFindings: [
      {
        direction: "negative",
        outcome:
          "Le transfert calé sur la réceptivité n'a pas amélioré les naissances vivantes vs. un timing standard dans un grand essai randomisé",
        magnitude: "58,5 % vs 61,9 % de naissances vivantes (JAMA RCT)",
        source: "Doyle et al., JAMA 2022",
      },
      {
        direction: "negative",
        outcome:
          "Le NICE n'a trouvé aucune preuve d'efficacité éligible pour EMMA ou ALICE, et pas de bénéfice significatif pour ERA",
        source: "NICE NG257, revue de preuves, 2026",
      },
      {
        direction: "negative",
        outcome:
          "La HFEA classe les tests de réceptivité endométriale « rouge » pour la majorité des patientes en fertilité",
        source: "HFEA, guide add-ons, 2023",
      },
    ],
    headlineStat: "Bundle non soutenu par les preuves",
  },
});

// ─── ERA ────────────────────────────────────────────────────────────

frTest("test-endometrial-receptivity-testing", {
  title: "Test de réceptivité endométriale (ERA, WIN-Test, MatriceLab)",
  subtitle:
    "Réservé aux échecs d'implantation répétés - non remboursé, hors PMA standard",
  body: {
    testName: "Test de réceptivité endométriale",
    soldAs: [
      {
        name: "ERA",
        company: "Igenomix",
        description:
          "Analyse de réceptivité endométriale par expression génique, classant l'échantillon en réceptif, pré-réceptif ou post-réceptif.",
        evidenceSummary:
          "ERA a les preuves cliniques les plus directes de la catégorie. Les résultats sont conflictuels, et le plus grand essai en aveugle n'a pas montré de bénéfice sur les naissances vivantes.",
      },
      {
        name: "ERPeak",
        company: "CooperSurgical",
        description:
          "Test de réceptivité endométriale par timing d'expression génique pour estimer la fenêtre d'implantation.",
        evidenceSummary:
          "Étude japonaise rétrospective en RIF avec de meilleurs résultats, mais pas de grand essai randomisé propre à ERPeak.",
      },
      {
        name: "beREADY",
        company: "Competence Centre on Health Technologies",
        description:
          "Test de réceptivité endométriale indiquant si la muqueuse semble calée pour le transfert.",
        evidenceSummary:
          "Modèle d'expression génique validé publié, mais pas d'essai propre à beREADY sur les naissances vivantes.",
      },
    ],
    whatItMeasures:
      "Mesure l'expression de gènes endométriaux pour classer la muqueuse comme réceptive, pré-réceptive ou post-réceptive. ERA est la marque Igenomix (~650-750 €, échantillon envoyé en Espagne). Des alternatives développées en France existent : WIN-Test et le bilan immunitaire MatriceLab Innove (Dr Nathalie Lédée, Paris) - ces tests sont sur une logique immunitaire, pas transcriptomique. ⚠️ Hors recommandations CNGOF/HAS, non remboursé par l'Assurance Maladie. Le grand essai randomisé Doyle 2022 (JAMA) n'a pas montré de bénéfice sur les naissances vivantes en usage routinier. Réservé aux échecs d'implantation répétés (RIF) en milieu privé.",
    howItWorks:
      "Le test est fait pendant un cycle simulé de TEC naturel ou médicamenté, souvent à P+5 en cycle médicamenté ou LH/hCG+7 en cycle naturel. Une biopsie endométriale est prise avec un fin cathéter en France. Pour ERA, l'échantillon part en Espagne ; WIN-Test/MatriceLab sont analysés en France. Le rapport peut suggérer de décaler le transfert. À discuter avec l'équipe AMP uniquement en cas d'échecs d'implantation répétés inexpliqués - pas un examen de PMA standard.",
    turnaroundTime: "2 à 3 semaines",
    cycleDay:
      "Timing de cycle simulé: souvent P+5 en cycle médicamenté ou LH/hCG+7 en cycle naturel.",
    howItWorksSteps: [
      "Ta clinique programme un cycle simulé naturel ou médicamenté de TEC, pas le cycle de transfert réel.",
      "La biopsie est calée sur la fenêtre d'implantation attendue : souvent P+5 en cycle médicamenté ou LH/hCG+7 en cycle naturel.",
      "Un praticien prélève une biopsie endométriale brève avec un fin cathéter.",
      "Le laboratoire analyse l'expression génique et classe l'échantillon comme réceptif, pré-réceptif, post-réceptif ou peu concluant.",
      "Si le rapport est non-réceptif, la clinique peut proposer d'ajuster l'exposition à la progestérone ou le timing du transfert au cycle suivant.",
    ],
    quickFacts: [
      { kind: "sample", value: "Biopsie de tissu endométrial" },
      { kind: "time", value: "Biopsie de 5 à 10 min" },
      { kind: "pain", value: "Crampes pendant et juste après" },
      { kind: "anesthesia", value: "En général aucune" },
      {
        kind: "prep",
        value: "Pas de jeûne en général ; demander pour l'ibuprofène",
      },
      { kind: "results", value: "2 à 3 semaines" },
    ],
    resultTypes: [
      {
        label: "Réceptif",
        meaning:
          "Le timing de la biopsie correspond à la fenêtre d'implantation prédite par le laboratoire.",
        whatToExpect:
          "Les cliniques gardent en général le timing standard. Ça ne garantit pas l'implantation et ne prouve pas que le test a amélioré tes chances.",
      },
      {
        label: "Pré-réceptif",
        meaning:
          "Le laboratoire prédit que la biopsie a été prise avant que l'endomètre atteigne sa fenêtre réceptive.",
        whatToExpect:
          "Le rapport peut suggérer plus d'exposition à la progestérone avant transfert. Les grands essais n'ont pas montré de bénéfice de routine sur les naissances vivantes.",
      },
      {
        label: "Post-réceptif",
        meaning:
          "Le laboratoire prédit que la biopsie a été prise après la fenêtre réceptive.",
        whatToExpect:
          "Le rapport peut suggérer moins de progestérone ou un transfert décalé. Demande précisément comment le plan changerait et pourquoi.",
      },
      {
        label: "Peu concluant",
        meaning:
          "L'échantillon ou le timing ne permet pas une interprétation fiable.",
        whatToExpect:
          "Une nouvelle biopsie peut être proposée — coût, inconfort et délai supplémentaires, sans bénéfice de routine prouvé.",
      },
    ],
    expectations: {
      canExpect: [
        "Un rapport de biopsie classant l'échantillon en réceptif, pré-réceptif ou post-réceptif",
        "Une proposition de timing de transfert « personnalisé » si le laboratoire estime la fenêtre décalée",
        "Les documents commerciaux plus anciens citent souvent 25-30 % de patientes avec une fenêtre décalée, mais ce chiffre ne s'est pas traduit par un bénéfice sur les naissances vivantes dans les essais de qualité",
        "Un cycle simulé avant le cycle de transfert réel",
      ],
      shouldNotExpect: [
        "Ne t'attends pas à ce que le résultat garantisse l'implantation ni n'explique tout transfert raté",
        "Ne t'attends pas à ce qu'il remplace la qualité embryonnaire, l'évaluation utérine, la technique de transfert ou le contexte clinique",
      ],
    },
    considerations: [
      "Demande précisément quel résultat changerait ton timing de transfert avant de faire la biopsie",
      "Prévois un cycle simulé, une biopsie brève, d'éventuelles crampes et un possible délai de transfert",
      "Demande si un résultat peu concluant ou décalé imposerait une nouvelle biopsie",
      "En cas de multiples transferts ratés, demande pourquoi ce test est recommandé pour ton cas précis",
    ],
    bestFor: [
      "Échec d'implantation répété — usage débattu",
      "Non recommandé en routine",
    ],
    keyFindings: [
      {
        direction: "negative",
        outcome:
          "Un essai randomisé n'a pas trouvé d'amélioration des naissances vivantes avec un transfert calé sur la réceptivité",
        magnitude:
          "Naissances vivantes : 58,5 % (réceptivité) vs 61,9 % (standard)",
        mechanism:
          "Le test vise à détecter une fenêtre d'implantation décalée, mais personnaliser le timing sur l'expression d'ARN n'a pas amélioré les résultats",
        source: "Doyle et al., JAMA 2022 (RCT, n=767)",
      },
      {
        direction: "negative",
        outcome:
          "Le NICE ne trouve pas de différence importante entre transfert congelé guidé par ERA et transfert standard sur les naissances vivantes ou la grossesse clinique",
        source: "NICE NG257, revue de preuves, 2026",
      },
      {
        direction: "negative",
        outcome:
          "La HFEA classe les tests de réceptivité endométriale « rouge » pour la majorité des patientes",
        source: "HFEA, guide add-ons, 2023",
      },
    ],
    headlineStat: "Pas de bénéfice sur la naissance vivante",
  },
});

// ─── Fertility hormone tracker ─────────────────────────────────────

frTest("test-fertility-tracker", {
  title: "Tracker hormonal de fertilité",
  subtitle: "Suit le pic de LH + confirme l'ovulation avec la progestérone",
  body: {
    testName: "Tracker d'ovulation et d'hormones",
    quickFacts: [
      { kind: "sample", value: "Bandelettes urinaires" },
      { kind: "time", value: "Quelques minutes par bandelette" },
      {
        kind: "prep",
        value: "Tester pendant la fenêtre fertile et après le pic",
      },
      { kind: "results", value: "Instantané par bandelette" },
    ],
    whatItMeasures:
      "Pic de LH (prédit l'ovulation 24 à 36 heures à l'avance) et PdG/progestérone (confirme que l'ovulation a bien eu lieu).",
    howItWorks:
      "Analyseur numérique réutilisable avec bandelettes et application compagnon. Les bandelettes LH détectent le pic, les bandelettes PdG confirment l'ovulation 7 à 10 jours plus tard. L'app suit les schémas sur plusieurs cycles.",
    turnaroundTime: "Résultats instantanés par bandelette",
    cycleDay:
      "Utilise les bandelettes LH pendant la fenêtre fertile; utilise les bandelettes PdG/progestérone 7 à 10 jours après le pic de LH ou l'ovulation suspectée.",
    markerTiming: [
      {
        marker: "Pic de LH",
        timing:
          "Tous les jours pendant la fenêtre fertile, ou selon les consignes du tracker",
        reason:
          "La LH monte peu avant l'ovulation, donc ce test est calé sur la fenêtre fertile attendue plutôt que sur les bilans de base du jour 2 à 4.",
      },
      {
        marker: "PdG / progestérone",
        timing:
          "Généralement 7 à 10 jours après le pic de LH ou l'ovulation suspectée",
        reason:
          "Les métabolites de la progestérone montent après l'ovulation, donc un test trop précoce peut manquer cette hausse.",
      },
    ],
  },
});

// ─── FSH ────────────────────────────────────────────────────────────

frTest("test-fsh", {
  title: "FSH",
  subtitle: "FSH au jour 2 à 4 pour évaluer la réserve ovarienne",
  body: {
    testName: "Hormone folliculo-stimulante (FSH)",
    quickFacts: [
      { kind: "sample", value: "Sang" },
      { kind: "time", value: "5 min de prélèvement" },
      { kind: "pain", value: "Piqûre d'aiguille" },
      { kind: "prep", value: "Jour 2 à 4 du cycle" },
      { kind: "results", value: "1 à 2 jours ouvrés" },
    ],
    whatItMeasures:
      "La FSH de base indique à quel point le cerveau travaille pour stimuler les ovaires. Elle est généralement interprétée avec l'œstradiol, car un œstradiol élevé en début de cycle peut supprimer la FSH et la rendre faussement rassurante.",
    howItWorks:
      "Prise de sang au jour 2 à 4 du cycle, le plus souvent jour 2 ou 3. Habituellement testée avec l'œstradiol et parfois la LH. À jeun généralement non requis, mais demande à ta clinique si elle est groupée avec d'autres analyses.",
    turnaroundTime: "1 à 2 jours ouvrés",
    cycleDay: "Jour 2 à 4, le plus souvent jour 2 ou 3",
    markerTiming: [
      {
        marker: "FSH",
        timing: "Jour 2 à 4, le plus souvent jour 2 ou 3",
        reason:
          "Le début de phase folliculaire montre le signal hypophysaire de base avant qu'un follicule dominant ne modifie le profil hormonal.",
      },
      {
        marker: "Œstradiol, si groupé",
        timing: "Jour 2 à 4",
        reason:
          "L'œstradiol aide à interpréter la FSH, car un œstradiol élevé en début de cycle peut supprimer la FSH.",
      },
    ],
  },
});

// ─── General bloodwork ─────────────────────────────────────────────

frTest("test-general-bloodwork", {
  title: "Bilan sanguin de fertilité",
  subtitle:
    "Prise de sang de routine pour mesurer œstradiol, LH, et progestérone",
  body: {
    testName: "Bilan sanguin de fertilité (E2, LH, progestérone)",
    quickFacts: [
      { kind: "sample", value: "Sang" },
      { kind: "time", value: "5 min de prélèvement" },
      { kind: "pain", value: "Piqûre d'aiguille" },
      { kind: "prep", value: "Prélèvement matinal si la clinique le demande" },
      { kind: "results", value: "Jour même, souvent en quelques heures" },
    ],
    whatItMeasures:
      "Mesure les hormones reproductives clés, le plus souvent œstradiol (E2), hormone lutéinisante (LH), et progestérone. Ces taux aident ton équipe à suivre la réponse ovarienne et à programmer les étapes critiques comme le déclenchement ou les transferts.",
    howItWorks:
      "Une infirmière préleveuse prend une petite quantité de sang dans une veine du bras, généralement au pli du coude. L'échantillon est envoyé au labo pour analyse hormonale, et les résultats sont généralement disponibles en quelques heures. Ta clinique peut combiner la prise de sang avec une échographie de suivi à la même visite pour limiter les déplacements. L'œstradiol reflète l'activité hormonale de tes follicules, la LH aide à détecter une montée d'ovulation imminente, et la progestérone confirme si l'ovulation a eu lieu ou soutient le suivi de phase lutéale. Arrive tôt le matin si on te le demande; beaucoup de cliniques prélèvent entre 7 h et 9 h. Reste hydratée la veille au soir et le matin pour faciliter la prise. Pas besoin d'être à jeun sauf indication contraire de ta clinique.",
    turnaroundTime: "Jour même (quelques heures)",
    cycleDay:
      "Variable: jour 2 à 4 pour l'E2/LH de base, répétée pendant le suivi, et environ 7 jours après l'ovulation pour la progestérone si l'objectif est de confirmer l'ovulation.",
    markerTiming: [
      {
        marker: "Œstradiol (E2)",
        timing:
          "Jour 2 à 4 pour la valeur de base; répété pendant la stimulation ou la préparation de TEC",
        reason:
          "L'E2 de base montre si les ovaires sont au repos. Pendant le traitement, la hausse d'E2 aide à suivre l'activité folliculaire ou la préparation de l'endomètre.",
      },
      {
        marker: "LH",
        timing:
          "Jour 2 à 4 pour la valeur de base; milieu de cycle si on recherche un pic",
        reason:
          "La LH de base aide à interpréter le signal cerveau-ovaire. Une hausse de LH en milieu de cycle est un autre usage, car elle peut annoncer l'ovulation.",
      },
      {
        marker: "Progestérone",
        timing:
          "Environ 7 jours après l'ovulation pour confirmer l'ovulation; selon le protocole pendant un TEC ou une stimulation",
        reason:
          "La progestérone monte après l'ovulation, donc son timing suit l'ovulation ou le calendrier de médicaments plutôt que le jour 2 à 4.",
      },
    ],
  },
});

// ─── Hormone panel ─────────────────────────────────────────────────

frTest("test-hormone-panel", {
  title: "Bilan hormonal complet",
  subtitle: "Kit à domicile: AMH plus FSH, LH et œstradiol au jour 2 à 4",
  body: {
    testName: "Bilan hormonal complet de fertilité",
    whyItMatters: [
      "L'AMH et le CFA situent la réserve ovarienne — ce sont des repères de planification, pas des cibles à optimiser à court terme.",
      "La FSH, la LH et l'œstradiol du jour 2 à 4 montrent le signal cerveau-ovaire de base avant qu'un follicule ne domine le cycle.",
      "Une FSH qui monte au jour 3, surtout avec une AMH ou un CFA bas, peut être un signal pour ne pas trop attendre.",
      "Ce bilan ne remplace pas la thyroïde, la vitamine D, la prolactine, le spermogramme ou l'échographie quand ils sont cliniquement pertinents.",
    ],
    quickFacts: [
      { kind: "sample", value: "Sang par piqûre au doigt" },
      { kind: "time", value: "10 à 15 min de collecte à domicile" },
      { kind: "pain", value: "Piqûre de lancette" },
      { kind: "prep", value: "Jour 2 à 4 pour FSH/LH/E2" },
      { kind: "results", value: "5 à 7 jours ouvrés" },
    ],
    whatItMeasures:
      "L'AMH estime la réserve ovarienne. La FSH, la LH et l'œstradiol sont interprétés comme valeurs de base en début de phase folliculaire, avant qu'un follicule dominant ne prenne le dessus.",
    howItWorks:
      "Recueille un échantillon sanguin par piqûre au doigt à domicile, généralement au jour 2 à 4 du cycle. Ce timing concerne la FSH, la LH et l'œstradiol de base. L'AMH est incluse dans le même prélèvement par praticité, mais elle peut être mesurée n'importe quel jour du cycle. Le kit inclut un dispositif de prélèvement, une enveloppe de retour prépayée et des instructions.",
    turnaroundTime: "5 à 7 jours ouvrés",
    cycleDay:
      "Jour 2 à 4 pour la FSH, la LH et l'œstradiol de base; AMH possible n'importe quel jour.",
    markerTiming: [
      {
        marker: "AMH",
        timing: "N'importe quel jour du cycle",
        reason:
          "L'AMH est relativement stable au cours du cycle, donc elle est groupée avec les bilans du jour 2 à 4 par praticité plutôt que par nécessité.",
      },
      {
        marker: "FSH",
        timing: "Jour 2 à 4, le plus souvent jour 2 ou 3",
        reason:
          "Le début de phase folliculaire montre le signal hypophysaire de base avant qu'un follicule dominant ne modifie le profil hormonal.",
      },
      {
        marker: "LH",
        timing:
          "Jour 2 à 4 pour la valeur de base; milieu de cycle seulement si on cherche le pic",
        reason:
          "La LH de base aide pour le contexte de réserve ovarienne et de PMOS (ex SOPK). La recherche d'un pic est une question différente, liée à l'ovulation.",
      },
      {
        marker: "Œstradiol (E2)",
        timing: "Jour 2 à 4 pour la base; répété pendant la stimulation",
        reason:
          "L'œstradiol précoce montre si les ovaires sont au repos et aide à interpréter la FSH, car un œstradiol élevé peut la supprimer.",
      },
    ],
  },
});

// ─── Infectious disease panel ──────────────────────────────────────

frTest("test-infectious-disease", {
  title: "Bilan infectieux",
  subtitle:
    "VIH, hépatites B et C, syphilis — obligatoires avant procédures d'AMP",
  body: {
    testName: "VIH/IST/bilan infectieux",
    quickFacts: [
      { kind: "sample", value: "Sang" },
      { kind: "time", value: "5 min de prélèvement par personne" },
      { kind: "pain", value: "Piqûre d'aiguille" },
      { kind: "prep", value: "En général pour les deux partenaires" },
      { kind: "results", value: "3 à 7 jours ouvrés" },
    ],
    whatItMeasures:
      "Dépistage du VIH-1 et 2, hépatite B (Ag HBs, anti-HBc, anti-HBs), hépatite C, syphilis (TPHA-VDRL), et HTLV-I/II pour les patientes issues de régions endémiques. Obligatoire pour les deux partenaires avant toute prise en charge en AMP en France, en application du Code de la santé publique (article R2142 et arrêtés AMP).",
    howItWorks:
      "Prise de sang pour les deux partenaires, en laboratoire d'analyses médicales. Les sérologies doivent être à jour : généralement valables 3 à 6 mois selon le délai entre prélèvement et tentative d'AMP, à refaire si dépassé. Une chlamydia ou un CMV peuvent être ajoutés selon le contexte.",
    turnaroundTime: "3 à 7 jours ouvrés",
  },
});

// ─── Karyotype ─────────────────────────────────────────────────────

frTest("test-karyotype", {
  title: "Caryotype",
  subtitle:
    "Examine le nombre et la structure des chromosomes chez les deux partenaires",
  body: {
    testName: "Caryotype",
    whatItMeasures:
      "Examine le nombre et la structure des chromosomes. Identifie les translocations, inversions, ou chromosomes manquants/supplémentaires causant fausses couches à répétition ou échecs d'implantation.",
    howItWorks:
      "Prise de sang — le labo met en culture les globules blancs et photographie les chromosomes au microscope. Prend plus de temps qu'une prise de sang standard.",
    turnaroundTime: "2 à 4 semaines",
    quickFacts: [
      { kind: "sample", value: "Sang" },
      { kind: "time", value: "5 min de prélèvement" },
      { kind: "pain", value: "Piqûre d'aiguille" },
      { kind: "prep", value: "Les deux partenaires si indiqué" },
      { kind: "results", value: "2 à 4 semaines" },
    ],
    resultTypes: [
      {
        label: "Caryotype normal (46,XX ou 46,XY)",
        meaning: "Le nombre et la structure des chromosomes sont normaux.",
        whatToExpect:
          "Ça exclut les anomalies chromosomiques constitutionnelles majeures comme facteur d'échec d'implantation ou de fausses couches à répétition.",
      },
      {
        label: "Anomalie détectée",
        meaning:
          "Le rapport identifie une translocation, une inversion ou un autre remaniement structurel.",
        whatToExpect:
          "Un conseil génétique est proposé. Selon l'anomalie, une FIV avec PGT-SR peut être discutée (en France, sur indication et validation par un CPDPN).",
      },
    ],
  },
});

// ─── Lining check ──────────────────────────────────────────────────

frTest("test-lining-check", {
  title: "Échographie de contrôle de l'endomètre",
  subtitle:
    "Échographie pour mesurer l'épaisseur endométriale pendant la prép de TEC",
  body: {
    testName: "Contrôle de l'endomètre",
    quickFacts: [
      { kind: "sample", value: "Échographie endovaginale" },
      { kind: "time", value: "5 à 10 min d'examen" },
      { kind: "pain", value: "Pression, généralement modérée" },
      {
        kind: "prep",
        value: "Vessie vide ; continuer les médicaments comme indiqué",
      },
      { kind: "results", value: "Pendant le rendez-vous" },
    ],
    whatItMeasures:
      "Mesure l'épaisseur et l'aspect de l'endomètre pendant un cycle de préparation au transfert d'embryon congelé (TEC). Votre centre interprète ces résultats avec le protocole, les analyses, le calendrier et vos antécédents pour décider de poursuivre, d'adapter ou de programmer le traitement.",
    howItWorks:
      "Le centre peut réaliser une échographie endovaginale pendant la préparation pour évaluer l'endomètre. Le calendrier du suivi et les éléments considérés comme favorables varient selon le protocole et le centre ; une seule épaisseur ou un seul aspect ne constitue pas une règle universelle de préparation. Votre équipe décide de poursuivre, d'adapter le plan ou de refaire un contrôle. Suivez l'ordonnance reçue et demandez au centre comment vous préparer à l'échographie et quels saignements ou spottings signaler.",
    cycleDay:
      "Préparation TEC — calendrier fixé par le protocole de votre centre",
  },
});

// ─── Monitoring ultrasound ─────────────────────────────────────────

frTest("test-monitoring-ultrasound", {
  title: "Échographie de suivi",
  subtitle:
    "Échographie endovaginale pour suivre la croissance des follicules et l'endomètre",
  body: {
    testName: "Échographie de suivi (contrôle folliculaire)",
    quickFacts: [
      { kind: "sample", value: "Échographie endovaginale" },
      { kind: "time", value: "5 à 10 min d'examen" },
      { kind: "pain", value: "Pression, généralement modérée" },
      {
        kind: "prep",
        value: "Vessie vide ; continuer les médicaments comme indiqué",
      },
      { kind: "results", value: "Pendant le rendez-vous" },
    ],
    whatItMeasures:
      "Mesure le nombre et la taille des follicules en développement et l'épaisseur de l'endomètre pendant la stimulation ovarienne. Ces échographies guident les décisions de dosage des médicaments tout au long de ton cycle.",
    howItWorks:
      "Une sonde d'échographie est délicatement insérée dans le vagin pour visualiser les ovaires et l'utérus. L'échographiste ou la médecin mesure chaque follicule visible en millimètres et enregistre l'épaisseur de l'endomètre. Les images apparaissent à l'écran en temps réel, et toute l'échographie prend généralement cinq à dix minutes. Selon les résultats, ton équipe peut ajuster les doses de médicaments, ajouter de nouveaux médicaments, ou programmer ton prochain rendez-vous de suivi. La plupart des cycles de stimulation demandent 3 à 6 rendez-vous de suivi espacés d'un à trois jours. Vide ta vessie avant l'échographie pour ton confort et porte des vêtements confortables faciles à enlever du bas. Continue tous les médicaments comme prescrits sauf indication contraire.",
    cycleDay: "Approximativement jours 6 à 12 du cycle (phase de stimulation)",
  },
});

// ─── PGT-A ─────────────────────────────────────────────────────────

frTest("test-preimplantation-genetic-testing", {
  title: "Test génétique préimplantatoire",
  subtitle: "Tests génétiques des embryons avant transfert",
  body: {
    testName: "Test génétique préimplantatoire (PGT)",
    howItWorksSteps: [
      "Les embryons grandissent au laboratoire de FIV jusqu'au stade blastocyste.",
      "Un embryologiste biopsie un petit nombre de cellules du trophectoderme (futur placenta).",
      "Les embryons sont en général vitrifiés pendant l'analyse génétique.",
      "Le laboratoire rend le type de résultat prévu : statut chromosomique, statut monogénique, réarrangement structurel ou score polygénique.",
      "Ta clinique vérifie ce qui est inclus dans le rapport pour ne pas payer deux fois un PGT-A ni passer à côté d'informations issues de la même biopsie.",
      "Les décisions de transfert combinent le résultat, la qualité embryonnaire, le contexte familial et le conseil génétique.",
    ],
    whatItMeasures:
      "Catégorie générale des tests génétiques réalisés sur les embryons pendant une FIV. Le PGT-A évalue le nombre de chromosomes, le PGT-M cible une maladie monogénique connue, le PGT-SR cible un réarrangement chromosomique structurel, et le PGT-P estime un risque polygénique pour certaines maladies complexes.",
    howItWorks:
      "La plupart des PGT commencent par une biopsie de quelques cellules du trophectoderme au stade blastocyste. Les embryons sont généralement vitrifiés pendant que l'échantillon est envoyé au laboratoire de génétique. Le résultat est ensuite interprété avec la qualité embryonnaire, l'âge, les antécédents familiaux et le conseil génétique.",
    quickFacts: [
      { kind: "sample", value: "Cellules de biopsie embryonnaire" },
      {
        kind: "time",
        value: "Pas de procédure supplémentaire pour la patiente",
      },
      { kind: "pain", value: "Aucune pour la patiente" },
      { kind: "anesthesia", value: "Aucune pour la biopsie" },
      { kind: "prep", value: "Nécessite une FIV avec des blastocystes" },
      { kind: "results", value: "Souvent 1 à 2 semaines" },
    ],
  },
});

frTest("test-pgt-a", {
  title: "PGT-A (dépistage embryonnaire)",
  subtitle:
    "Non autorisé en France pour la PMA standard - n'est légal que sur indication PGT-M/SR",
  body: {
    testName: "Test génétique préimplantatoire pour aneuploïdie (PGT-A)",
    whatItMeasures:
      "Les 23 paires de chromosomes dans chaque embryon — identifie les embryons euploïdes (normaux), aneuploïdes (anormaux), et mosaïques. ⚠️ Important : la loi française de bioéthique et l'Agence de la biomédecine n'autorisent pas le PGT-A en routine. Seuls le PGT-M (maladies monogéniques graves) et le PGT-SR (remaniements structuraux) sont autorisés, sur indication précise validée par un CPDPN.",
    howItWorks:
      "L'embryologiste fait une biopsie de 5 à 10 cellules du trophectoderme au jour 5 ou 6 du développement embryonnaire. Les embryons sont vitrifiés pendant que les cellules sont envoyées à un labo de génétique. Les résultats classifient les embryons comme euploïdes, aneuploïdes, ou mosaïques. En France, cette procédure n'est pas disponible en PMA publique ; certaines patientes vont à l'étranger (Espagne, Belgique) pour y accéder.",
    turnaroundTime: "7 à 10 jours ouvrés",
    quickFacts: [
      { kind: "sample", value: "Cellules de biopsie embryonnaire" },
      { kind: "results", value: "7 à 10 jours ouvrés" },
    ],
    resultTypes: [
      {
        label: "Euploïde",
        meaning:
          "Le nombre de chromosomes attendu est retrouvé pour l'embryon testé.",
        whatToExpect:
          "Un embryon euploïde est prioritaire pour le transfert. Ça n'élimine pas tout risque de fausse couche ni n'affecte tout facteur utérin.",
      },
      {
        label: "Aneuploïde",
        meaning: "Un chromosome supplémentaire ou manquant est détecté.",
        whatToExpect:
          "L'embryon n'est en général pas transféré. La distinction entre erreur méiotique et mitotique n'est pas toujours possible sur biopsie.",
      },
      {
        label: "Mosaïque",
        meaning: "L'échantillon mélange des cellules euploïdes et aneuploïdes.",
        whatToExpect:
          "L'interprétation dépend du pourcentage et du chromosome concerné ; un conseil génétique aide à décider si un transfert est envisagé.",
      },
    ],
    expectations: {
      canExpect: [
        "Une classification embryon par embryon en euploïde, aneuploïde ou mosaïque",
        "Une priorisation possible des embryons pour le transfert selon le résultat",
      ],
      shouldNotExpect: [
        "En France, le PGT-A n'est pas autorisé en PMA de routine — seuls PGT-M et PGT-SR le sont sur indication CPDPN",
        "Ne remplace ni la qualité embryonnaire, ni l'évaluation utérine, ni le contexte clinique",
      ],
    },
    considerations: [
      "Discuter d'un accès à l'étranger implique un cadre légal et financier différent — un conseil génétique est utile en amont",
      "Le résultat de biopsie ne reflète pas parfaitement l'ensemble de l'embryon (mosaïcisme, limite du prélèvement)",
      "Un résultat aneuploïde ne signifie pas systématiquement une origine parentale",
    ],
    bestFor: [
      "Situations validées par CPDPN pour PGT-M ou PGT-SR",
      "Contextes de recherche pour le PGT-A",
    ],
    keyFindings: [
      {
        direction: "neutral",
        outcome:
          "Le PGT-A peut augmenter le taux d'implantation par transfert d'un embryon euploïde, mais son bénéfice sur les naissances vivantes cumulées reste débattu",
        source: "STAR trial, Munné et al., Fertility and Sterility 2019",
      },
    ],
    headlineStat: "Non autorisé en PMA de routine en France",
  },
});

// ─── PGT-M ─────────────────────────────────────────────────────────

frTest("test-pgt-m", {
  title: "PGT-M (maladies monogéniques)",
  subtitle: "Teste les embryons pour une mutation génétique connue précise",
  body: {
    testName:
      "Test génétique préimplantatoire pour maladies monogéniques (PGT-M)",
    howItWorksSteps: [
      "Un conseiller ou un laboratoire de génétique confirme la variante familiale exacte et le mode de transmission.",
      "Le laboratoire crée ou valide une mise au point PGT-M sur mesure, à partir de l'ADN des futurs parents et parfois de proches.",
      "Les embryons sont obtenus par FIV et biopsiés au stade blastocyste.",
      "Le laboratoire teste l'ADN des cellules biopsiées pour la variante familiale ou des marqueurs liés.",
      "Le rapport classe les embryons comme non atteints, porteurs, atteints ou à haut risque, ou sans résultat selon la maladie et la méthode.",
    ],
    whatItMeasures:
      "Teste les embryons pour une mutation génétique précise et connue (par exemple mucoviscidose, drépanocytose, BRCA). Contrairement au PGT-A qui vérifie les chromosomes, le PGT-M cible un trouble monogénique identifié par dépistage de porteur.",
    howItWorks:
      "Demande la création d'une sonde personnalisée (4 à 6 semaines) à partir de l'ADN des deux partenaires et parfois de membres de la famille. Ensuite, les cellules biopsiées sont testées contre cette sonde. Seuls les embryons sans la mutation sont transférés.",
    turnaroundTime:
      "1 à 2 semaines après biopsie (création de la sonde : 4 à 6 semaines en amont)",
    quickFacts: [
      { kind: "sample", value: "Biopsie embryonnaire + ADN familial" },
      {
        kind: "time",
        value: "Pas de procédure supplémentaire pour la patiente",
      },
      { kind: "pain", value: "Aucune pour la patiente" },
      { kind: "anesthesia", value: "Aucune pour la biopsie" },
      { kind: "prep", value: "Mise au point sur mesure en amont" },
      { kind: "results", value: "1 à 2 semaines après biopsie" },
    ],
    resultTypes: [
      {
        label: "Non atteint",
        meaning:
          "L'embryon ne porte pas la mutation ciblée (ou porte un seul allèle, non atteint clinique).",
        whatToExpect:
          "Peut être proposé pour transfert selon l'ensemble du bilan.",
      },
      {
        label: "Atteint",
        meaning: "L'embryon porte les allèles pathogènes ciblés.",
        whatToExpect:
          "L'embryon n'est pas transféré. Le conseil génétique passe en revue les autres embryons disponibles.",
      },
    ],
    expectations: {
      canExpect: [
        "Une sonde personnalisée créée à partir de ton ADN et de celui de ton partenaire",
        "Un délai en amont pour la mise au point du test",
        "Une prise en charge en France sur indication CPDPN, remboursée dans le cadre de la loi de bioéthique",
      ],
      shouldNotExpect: [
        "Ne détecte pas d'autres mutations que celle ciblée",
        "Ne remplace pas le PGT-A pour les anomalies de nombre de chromosomes",
      ],
    },
    considerations: [
      "L'indication est validée par un centre pluridisciplinaire de diagnostic prénatal (CPDPN)",
      "La mise au point de la sonde peut prendre plusieurs semaines — anticipe ce délai avec ton centre",
      "Un conseil génétique en amont explique les options : conception naturelle avec DPN, DPI, don, adoption",
    ],
    bestFor: [
      "Maladies monogéniques graves à révélation précoce, sur indication CPDPN",
      "Couples porteurs identifiés à haut risque de transmission",
    ],
    keyFindings: [
      {
        direction: "positive",
        outcome:
          "Le PGT-M est reconnu et remboursé en France sur indication CPDPN dans le cadre de la loi de bioéthique",
        source: "Agence de la biomédecine, loi de bioéthique",
      },
    ],
  },
});

// ─── PGT-SR ────────────────────────────────────────────────────────

frTest("test-pgt-sr", {
  title: "PGT-SR (réarrangements structurels)",
  subtitle:
    "Teste les embryons pour les réarrangements chromosomiques structurels",
  body: {
    testName:
      "Test génétique préimplantatoire pour réarrangements structurels (PGT-SR)",
    howItWorksSteps: [
      "Un caryotype ou un test chromosomique parental identifie le réarrangement structurel.",
      "Le laboratoire de génétique confirme que sa méthode peut détecter les résultats déséquilibrés attendus chez l'embryon.",
      "Les embryons sont obtenus par FIV et biopsiés au stade blastocyste.",
      "Le laboratoire teste l'ADN des cellules biopsiées pour repérer les gains ou pertes chromosomiques liés au réarrangement.",
      "La clinique revoit quels embryons sont normaux ou équilibrés, déséquilibrés, mosaïques ou segmentaires, ou sans résultat.",
    ],
    whatItMeasures:
      "Teste les embryons pour les réarrangements chromosomiques structurels — translocations, inversions, délétions. Pour les parents avec translocation équilibrée connue causant des fausses couches à répétition.",
    howItWorks:
      "Semblable au PGT-A mais spécifiquement conçu pour détecter les réarrangements déséquilibrés. Les cellules biopsiées sont analysées par séquençage nouvelle génération. Seuls les embryons équilibrés/normaux sont transférés.",
    turnaroundTime: "1 à 2 semaines après biopsie",
    quickFacts: [
      { kind: "sample", value: "Cellules de biopsie embryonnaire" },
      {
        kind: "time",
        value: "Pas de procédure supplémentaire pour la patiente",
      },
      { kind: "pain", value: "Aucune pour la patiente" },
      { kind: "anesthesia", value: "Aucune pour la biopsie" },
      { kind: "prep", value: "Nécessite un caryotype parental connu" },
      { kind: "results", value: "1 à 2 semaines après biopsie" },
    ],
    resultTypes: [
      {
        label: "Équilibré ou normal",
        meaning:
          "L'embryon présente un profil chromosomique équilibré ou normal pour le remaniement recherché.",
        whatToExpect:
          "Peut être proposé pour transfert selon l'ensemble du bilan.",
      },
      {
        label: "Déséquilibré",
        meaning:
          "L'embryon porte un déséquilibre chromosomique lié au remaniement parental.",
        whatToExpect:
          "L'embryon n'est pas transféré. Le conseil génétique passe en revue les autres embryons.",
      },
    ],
    expectations: {
      canExpect: [
        "Une analyse par séquençage nouvelle génération pour détecter les déséquilibres",
        "Une prise en charge en France sur indication CPDPN, remboursée",
      ],
      shouldNotExpect: [
        "Ne remplace pas le PGT-A pour toutes les anomalies chromosomiques de novo",
        "Ne détecte pas les mutations monogéniques (voir PGT-M)",
      ],
    },
    considerations: [
      "Un caryotype parental documentant le remaniement est en général requis avant PGT-SR",
      "Le conseil génétique prépare l'interprétation des résultats et les décisions de transfert",
    ],
    bestFor: [
      "Remaniements chromosomiques équilibrés parentaux (translocations, inversions), sur indication CPDPN",
      "Fausses couches à répétition liées à un remaniement structurel documenté",
    ],
  },
});

// ─── PGT-P ─────────────────────────────────────────────────────────

frTest("test-pgt-p", {
  title: "PGT-P (score de risque polygénique)",
  subtitle: "Interdit en France - non recommandé en clinique par l'ASRM 2026",
  body: {
    testName: "Test génétique préimplantatoire pour risque polygénique (PGT-P)",
    howItWorksSteps: [
      "Les embryons sont biopsiés pendant la FIV, généralement au stade blastocyste.",
      "Le laboratoire séquence la biopsie plus largement ou combine les données PGT-A déjà obtenues avec les génomes parentaux.",
      "Le rapport doit préciser si un dépistage d'aneuploïdie PGT-A est inclus — de nombreux workflows élargis l'incluent déjà.",
      "Un modèle polygénique estime un risque relatif ou absolu pour certaines conditions complexes sélectionnées.",
      "Le rapport peut comparer les embryons entre eux, après avoir pris en compte le statut chromosomique, la qualité embryonnaire et les risques monogéniques connus.",
      "Un professionnel de la génétique doit expliquer quelles couches viennent de la même biopsie et ce que chacune apporte.",
    ],
    whatItMeasures:
      "Utilise des scores de risque polygénique pour estimer la prédisposition génétique relative d'un embryon pour certaines maladies complexes. Ce n'est pas un diagnostic et cela ne garantit pas la santé future de l'enfant. ⚠️ Le PGT-P est interdit en France par la loi de bioéthique : la sélection d'embryons sur des scores polygéniques n'est pas légalement accessible. L'ASRM (USA) recommande aussi de ne pas le proposer en routine clinique à ce stade.",
    howItWorks:
      "Le laboratoire combine les données génétiques de la biopsie embryonnaire avec l'ADN parental et des modèles de risque issus de populations. Le résultat peut classer des embryons selon un risque estimé, mais doit être interprété avec un conseil génétique. En pratique, ce test n'est pas disponible dans les centres AMP français ; certaines personnes le rencontrent uniquement dans le cadre d'études cliniques ou à l'étranger.",
    turnaroundTime: "Dépend du laboratoire",
    quickFacts: [
      { kind: "sample", value: "Biopsie embryonnaire + ADN parental" },
      {
        kind: "time",
        value: "Pas de procédure supplémentaire pour la patiente",
      },
      { kind: "pain", value: "Aucune pour la patiente" },
      { kind: "anesthesia", value: "Aucune pour la biopsie" },
      {
        kind: "prep",
        value: "Nécessite une FIV avec assez d'embryons à comparer",
      },
      { kind: "results", value: "Dépend du laboratoire" },
    ],
    expectations: {
      canExpect: [
        "Un score de risque estimé, non diagnostique",
        "Ce test n'est pas accessible en France par le circuit AMP légal",
      ],
      shouldNotExpect: [
        "Ne prédit pas la santé future de l'enfant",
        "N'est pas recommandé en routine clinique par les sociétés savantes majeures",
      ],
    },
    considerations: [
      "En France, la sélection d'embryons sur des scores polygéniques n'est pas légalement accessible.",
      "L'estimation dépend de la population de référence — la validité chez des ancêtres non européennes est plus faible.",
      "Un conseil génétique est indispensable avant tout usage à l'étranger.",
    ],
    bestFor: [
      "Uniquement dans le cadre d'études cliniques",
      "Non recommandé en clinique de routine",
    ],
    keyFindings: [
      {
        direction: "negative",
        outcome:
          "Les sociétés savantes majeures (ESHRE, ASRM) ne soutiennent pas l'usage clinique de routine du PGT-P",
        source: "ESHRE 2022 ; ASRM 2024",
      },
    ],
    headlineStat: "Interdit en France en PMA",
  },
});

// ─── Progesterone check ────────────────────────────────────────────

frTest("test-progesterone-check", {
  title: "Progestérone",
  subtitle:
    "Prise de sang pour confirmer les taux de progestérone et soutenir le cycle",
  body: {
    testName: "Dosage sanguin de progestérone",
    quickFacts: [
      { kind: "sample", value: "Sang" },
      { kind: "time", value: "5 min de prélèvement" },
      { kind: "pain", value: "Piqûre d'aiguille" },
      { kind: "prep", value: "Ne saute pas la dose de progestérone prescrite" },
      { kind: "results", value: "Jour même, souvent en quelques heures" },
    ],
    whatItMeasures:
      "Mesure la progestérone sérique, l'hormone essentielle à la préparation de l'endomètre pour l'implantation et au maintien de la grossesse précoce. Vérifiée pour confirmer l'ovulation, contrôler les taux de supplémentation, ou évaluer le soutien de phase lutéale.",
    howItWorks:
      "Un petit échantillon de sang est prélevé d'une veine du bras. Le labo mesure la concentration de progestérone en nanogrammes par millilitre (ng/mL). En cycle naturel, la progestérone monte après l'ovulation, et un taux au-dessus de 3 ng/mL confirme généralement que l'ovulation a eu lieu. Un taux au-dessus de 10 à 15 ng/mL peut indiquer une phase lutéale solide. Pendant un cycle de TEC médicamenté, ta clinique peut vérifier la progestérone la veille ou le jour du transfert pour confirmer que ton endomètre a été adéquatement exposé. Si les taux sont bas, ta médecin peut augmenter la dose de progestérone (vaginale, intramusculaire, ou orale) ou repousser le transfert. Prends tes compléments de progestérone comme prescrits. Ne saute pas une dose avant le test. Note l'heure de ta dernière dose, car ta clinique pourra te le demander. Les prises matinales sont généralement préférées pour la cohérence.",
    turnaroundTime: "Jour même (quelques heures)",
    cycleDay:
      "Environ 7 jours après l'ovulation pour confirmer l'ovulation en cycle naturel; selon le protocole pendant un TEC ou une stimulation.",
    markerTiming: [
      {
        marker: "Progestérone",
        timing:
          "Environ 7 jours après l'ovulation pour confirmer l'ovulation; selon le protocole pendant un TEC ou une stimulation",
        reason:
          "La progestérone est basse avant l'ovulation et monte ensuite, donc un test au jour 21 ne vaut que pour quelqu'un qui ovule autour du jour 14.",
      },
    ],
  },
});

// ─── Prolactin ─────────────────────────────────────────────────────

frTest("test-prolactin", {
  title: "Prolactine",
  subtitle: "Dépistage d'une prolactine élevée pouvant supprimer l'ovulation",
  body: {
    testName: "Prolactine",
    quickFacts: [
      { kind: "sample", value: "Sang" },
      { kind: "time", value: "5 min de prélèvement" },
      { kind: "pain", value: "Piqûre d'aiguille" },
      { kind: "prep", value: "Souvent 3 à 4 heures après le réveil" },
      { kind: "results", value: "1 à 3 jours ouvrés" },
    ],
    whatItMeasures:
      "Une prolactine élevée peut supprimer l'ovulation. Dépiste l'hyperprolactinémie.",
    howItWorks:
      "Prise de sang, souvent programmée 3 à 4 heures après le réveil. Certaines cliniques demandent d'être à jeun. Le stress et la stimulation mammaire récente peuvent temporairement élever les taux, donc une valeur haute inattendue peut être recontrôlée dans des conditions plus calmes.",
    turnaroundTime: "1 à 3 jours ouvrés",
    cycleDay:
      "N'importe quel jour du cycle; le timing après le réveil et le niveau de stress comptent plus que le jour du cycle.",
    markerTiming: [
      {
        marker: "Prolactine",
        timing:
          "N'importe quel jour du cycle, souvent 3 à 4 heures après le réveil",
        reason:
          "La prolactine varie dans la journée et peut monter avec le stress, l'exercice, le sommeil ou la stimulation mammaire. Un recontrôle est parfois utilisé pour confirmer un taux élevé.",
      },
    ],
  },
});

// ─── Saline sonogram ──────────────────────────────────────────────

// ─── Saline sonogram (hystérosonographie) ───────────────────────────

frTest("test-saline-sonogram", {
  title: "Hystérosonographie (SIS)",
  subtitle:
    "Échographie avec injection de sérum physiologique pour évaluer la cavité utérine",
  body: {
    testName: "Hystérosonographie (SIS)",
    quickFacts: [
      { kind: "sample", value: "Échographie avec sérum physiologique" },
      { kind: "time", value: "10 à 15 min de procédure" },
      { kind: "pain", value: "Brèves crampes ou pression" },
      { kind: "anesthesia", value: "En général aucune" },
      { kind: "prep", value: "Jours 6 à 12 du cycle ; ibuprofène si possible" },
      { kind: "results", value: "Pendant le rendez-vous" },
    ],
    whatItMeasures:
      "Évalue l'intérieur de la cavité utérine à l'aide de sérum physiologique stérile et d'une échographie endovaginale. Peut détecter des polypes, fibromes, tissu cicatriciel, et autres anomalies structurelles pouvant interférer avec l'implantation.",
    howItWorks:
      "Tu es allongée sur la table d'examen et un spéculum est mis en place, comme pour un frottis. Un fin cathéter souple est passé à travers le col et une petite quantité de sérum physiologique stérile est lentement injectée dans la cavité utérine. Le sérum dilate la cavité, permettant à la médecin de voir clairement ses contours à l'échographie endovaginale. Toute la procédure dure environ 10 à 15 minutes. Le sérum est naturellement absorbé et évacué par le corps. Si des anomalies sont trouvées — comme un polype ou un fibrome sous-muqueux — ta médecin peut recommander une hystéroscopie pour le retirer avant de poursuivre le traitement. Programme entre les jours 6 et 12 du cycle (après tes règles, avant l'ovulation). Prends 400 à 600 mg d'ibuprofène 30 à 60 minutes avant la procédure pour réduire les crampes. Tu peux ressentir un bref inconfort à la mise en place du cathéter et une sensation de pression ou de légères crampes pendant que le sérum remplit l'utérus. Les résultats sont discutés immédiatement après la procédure.",
    cycleDay: "Phase folliculaire (jours 6 à 12 du cycle)",
  },
});

// ─── Semen analysis ───────────────────────────────────────────────

frTest("test-semen-analysis", {
  title: "Spermogramme à domicile",
  subtitle: "Kits de collecte à domicile — ou visite rapide en clinique",
  body: {
    testName: "Spermogramme",
    whatItMeasures:
      "Numération, concentration, morphologie, mobilité progressive et non progressive, agglutination, spermatozoïdes immobiles, pH et volume. Selon le panel choisi, le bilan peut aussi inclure les anticorps antispermatozoïdes, la fragmentation de l'ADN spermatique et la méthylation de l'ADN spermatique.",
    howItWorks:
      "Recueil de l'échantillon par kit à domicile envoyé au labo, ou recueilli en clinique. Abstinence de 2 à 5 jours avant. Les paramètres avancés dépendent du panel choisi et sont interprétés avec les paramètres de base et le contexte clinique.",
    turnaroundTime: "3 à 5 jours ouvrés",
    quickFacts: [
      { kind: "sample", value: "Sperme" },
      { kind: "time", value: "Kit à domicile ou recueil en clinique" },
      { kind: "prep", value: "Abstinence 2 à 5 jours" },
      { kind: "results", value: "3 à 5 jours ouvrés" },
    ],
    expectations: {
      canExpect: [
        "Un rapport sur la numération, la mobilité, la morphologie et le volume",
        "Une valeur unique — une analyse de contrôle 4 à 6 semaines plus tard est souvent proposée avant de conclure",
      ],
      shouldNotExpect: [
        "Un seul résultat anormal n'est pas diagnostique — la variabilité est importante",
        "Ne remplace pas un bilan andrologique clinique en cas d'anomalie",
      ],
    },
    considerations: [
      "Pas de lubrifiant pendant le recueil (souvent spermicide)",
      "Éviter alcool, drogues récréatives et bains chauds au moins 72 h avant",
      "Un délai de 4 à 6 semaines entre deux tests permet le renouvellement des spermatozoïdes",
    ],
  },
});

frDataPoint("dp-sperm-count", {
  title: "Numération spermatique",
  body: {
    normalRange: "15 à 200 millions/mL",
    interpretation:
      "En dessous de 15 M/mL est considéré bas (oligospermie). Ne panique pas — des changements de mode de vie peuvent améliorer les chiffres en 3 mois.",
  },
});

frDataPoint("dp-sperm-motility", {
  title: "Mobilité spermatique",
  body: {
    normalRange: "40 % ou plus en mouvement vers l'avant",
    interpretation:
      "La mobilité progressive correspond à des spermatozoïdes nageant en ligne droite. En dessous de 40 % peut indiquer un problème de mobilité.",
  },
});

frDataPoint("dp-sperm-morphology", {
  title: "Morphologie spermatique",
  body: {
    normalRange: "4 % ou plus avec une forme normale",
    interpretation:
      "Critères stricts (Kruger). Même 4 % de formes normales est considéré adéquat. La morphologie est le marqueur le moins prédictif.",
  },
});

// ─── Thrombophilia panel ──────────────────────────────────────────

frTest("test-thrombophilia", {
  title: "Bilan de thrombophilie",
  subtitle:
    "Dépistage des troubles de la coagulation liés aux fausses couches à répétition",
  body: {
    testName: "Bilan de thrombophilie",
    whatItMeasures:
      "Tests pour les troubles de la coagulation (Facteur V Leiden, mutation de la prothrombine, anticorps antiphospholipides, protéines C/S). Important dans l'évaluation des fausses couches à répétition.",
    howItWorks:
      "Prise de sang, laboratoire spécialisé. Peut inclure anticoagulant lupique, anticorps anticardiolipine, anti-bêta-2-glycoprotéine. Certains tests demandent d'être à jeun.",
    turnaroundTime: "5 à 10 jours ouvrés",
    quickFacts: [
      { kind: "sample", value: "Sang" },
      { kind: "time", value: "5 min de prélèvement" },
      { kind: "pain", value: "Piqûre d'aiguille" },
      {
        kind: "prep",
        value: "Recontrôle des anticorps antiphospholipides si positifs",
      },
      { kind: "results", value: "5 à 10 jours ouvrés" },
    ],
    expectations: {
      canExpect: [
        "Un panel combinant génétique (Facteur V, prothrombine) et anticorps (antiphospholipides)",
        "Une confirmation à 12 semaines si des anticorps sont positifs",
      ],
      shouldNotExpect: [
        "Un résultat isolé ne pose pas seul un diagnostic de syndrome des antiphospholipides",
        "Un dépistage systématique n'est pas recommandé en dehors d'une indication clinique",
      ],
    },
    considerations: [
      "L'indication typique en France est le bilan de fausses couches à répétition ou d'échecs d'implantation répétés",
      "Les résultats orientent d'éventuelles décisions d'anticoagulation en grossesse",
    ],
    bestFor: [
      "Fausses couches à répétition, échecs d'implantation répétés",
      "Antécédent thrombo-embolique personnel ou familial",
    ],
    keyFindings: [
      {
        direction: "neutral",
        outcome:
          "Le bilan est pertinent dans des indications ciblées ; le dépistage universel n'est pas recommandé",
        source: "CNGOF, HAS ; ACOG committee opinion",
      },
    ],
    headlineStat: "Sur indication, pas en routine",
  },
});

// ─── Thyroid panel ────────────────────────────────────────────────

frTest("test-thyroid-panel", {
  title: "Bilan thyroïdien",
  subtitle: "TSH, T3, et T4 pour dépister un dysfonctionnement thyroïdien",
  body: {
    testName: "Bilan thyroïdien (TSH/T3/T4)",
    whyItMatters: [
      "Si la TSH est élevée, un traitement thyroïdien peut la ramener dans la cible « fertilité » en environ 6 à 8 semaines.",
      "Les anticorps thyroïdiens peuvent influencer l'intensité du suivi thyroïdien avant et pendant la grossesse.",
      "Ce test n'est pas cycle-dépendant : il peut être répété lors d'un changement de médicament ou de symptômes.",
    ],
    quickFacts: [
      { kind: "sample", value: "Sang" },
      { kind: "time", value: "5 min de prélèvement" },
      { kind: "pain", value: "Piqûre d'aiguille" },
      { kind: "prep", value: "Demander s'il faut arrêter la biotine" },
      { kind: "results", value: "1 à 3 jours ouvrés" },
    ],
    whatItMeasures:
      "La TSH dépiste un dysfonctionnement thyroïdien. Des taux anormaux peuvent affecter l'ovulation, l'implantation et les résultats de grossesse.",
    howItWorks:
      "Simple prise de sang, n'importe quel labo. La TSH est le dépistage principal ; T3/T4 sont ajoutés si anormal. Demande à ta clinique s'il faut arrêter la biotine 48 à 72 heures avant le test, car elle peut interférer avec certains dosages thyroïdiens.",
    turnaroundTime: "1 à 3 jours ouvrés",
    cycleDay:
      "N'importe quel jour du cycle; garde des conditions de test cohérentes et demande s'il faut arrêter la biotine.",
    markerTiming: [
      {
        marker: "TSH",
        timing: "N'importe quel jour du cycle",
        reason:
          "La TSH n'est pas calée sur le cycle menstruel, mais des conditions de test cohérentes aident à interpréter les résultats dans le temps.",
      },
      {
        marker: "T4 libre / T3 libre",
        timing: "N'importe quel jour du cycle",
        reason:
          "Les hormones thyroïdiennes ne sont pas spécifiques à un jour du cycle. La biotine peut interférer avec certains dosages thyroïdiens, donc ta clinique peut demander de l'arrêter avant le test.",
      },
    ],
  },
});

// ─── Vitamin D ────────────────────────────────────────────────────

frTest("test-vitamin-d", {
  title: "Vitamine D",
  subtitle:
    "Taux de vitamine D liés à la fertilité et aux résultats d'implantation",
  body: {
    testName: "Vitamine D 25-OH",
    whyItMatters: [
      "La vitamine D est un des marqueurs de base les plus actionnables, parce qu'une carence se corrige généralement bien avec une supplémentation.",
      "Certains programmes de fertilité visent au-dessus de 40 ng/mL, mais la cible exacte doit être adaptée aux antécédents et aux plages de référence du laboratoire.",
      "Après démarrage ou changement de supplémentation, beaucoup de cliniciens recontrôlent après environ 8 semaines.",
    ],
    quickFacts: [
      { kind: "sample", value: "Sang" },
      { kind: "time", value: "5 min de prélèvement ou kit à domicile" },
      { kind: "pain", value: "Piqûre d'aiguille ou de lancette" },
      {
        kind: "prep",
        value: "N'importe quel jour du cycle ; jeûne en général non requis",
      },
      { kind: "results", value: "2 à 5 jours ouvrés" },
    ],
    whatItMeasures:
      "Taux de vitamine D liés aux résultats de fertilité. La carence est associée à des taux d'implantation et de grossesse plus bas.",
    howItWorks:
      "Simple prise de sang, n'importe quel labo. Peut être faite n'importe quel jour du cycle. À jeun généralement non requis.",
    turnaroundTime: "2 à 5 jours ouvrés",
    cycleDay: "N'importe quel jour du cycle",
    markerTiming: [
      {
        marker: "Vitamine D 25-OH",
        timing: "N'importe quel jour du cycle",
        reason:
          "Le statut en vitamine D n'est pas lié à un jour précis du cycle menstruel, donc le même test peut servir au bilan initial et au suivi.",
      },
    ],
  },
});

// ─── Whole genome sequencing ─────────────────────────────────────

frTest("test-whole-genome", {
  title: "Séquençage du génome entier",
  subtitle:
    "Plus large que le dépistage de porteur : portage, variants diagnostiques et métabolisme des médicaments",
  body: {
    testName: "Séquençage du génome entier (WGS)",
    quickFacts: [
      { kind: "sample", value: "Sang ou salive" },
      { kind: "time", value: "5 min de prélèvement ou kit salivaire" },
      { kind: "pain", value: "Piqûre d'aiguille si sang" },
      {
        kind: "prep",
        value: "Consentement génétique et préférences de rapport",
      },
      { kind: "results", value: "2 à 3 semaines" },
    ],
    expectations: {
      canExpect: [
        "Un dossier génétique large réutilisable pour la fertilité, la grossesse et les décisions médicamenteuses",
        "Un rapport interprété par un conseiller en génétique",
      ],
      shouldNotExpect: [
        "Ce test n'est pas accessible en direct au consommateur en France (article 16-10 du Code civil)",
        "Ne prédit pas tout et ne pose pas un diagnostic à lui seul",
      ],
    },
    considerations: [
      "Une prescription médicale et un conseil pré/post-test sont légalement requis en France",
      "Le rapport peut inclure des résultats inattendus (variants de risque héréditaire) — le consentement doit anticiper ce point",
    ],
    whatItMeasures:
      "Une lecture large de ton ADN pouvant servir à plusieurs types de rapports : statut de porteur reproductif, résultats monogéniques, variants de risque héréditaire comme BRCA1 ou BRCA2, métabolisme des médicaments et estimations de risque polygénique pour certaines conditions fréquentes. L'objectif est de créer un dossier génétique réutilisable pour la planification de fertilité aujourd'hui et réanalysable à mesure que la science évolue. Cela ne prédit pas tout et ne pose pas un diagnostic à lui seul. ⚠️ En France, le séquençage du génome entier à usage individuel est strictement encadré : les tests génétiques directs au consommateur sont interdits (article 16-10 du Code civil), et tout test prédictif requiert une prescription médicale et un conseil pré/post-test. Le WGS à visée reproductive en accès consommateur n'est pas légalement accessible en France.",
    howItWorks:
      "Un échantillon de sang ou de salive est recueilli, selon le kit et le laboratoire. L'ADN est extrait, séquencé, analysé pour différents types de résultats génétiques, puis transformé en rapport pouvant être interprété dans le contexte de la fertilité, du projet familial et des décisions médicamenteuses.",
    howItWorksSteps: [
      "Tu fournis un échantillon de sang ou de salive. Le sang peut être prélevé en laboratoire ou, lorsque c'est disponible, par phlébotomie mobile à domicile ou au travail ; la salive peut généralement être recueillie avec un kit.",
      "Le laboratoire extrait l'ADN de l'échantillon et le passe dans une machine de séquençage qui lit les lettres de l'ADN à travers le génome.",
      "Des logiciels de bio-informatique alignent ces lectures sur un génome de référence et recherchent des variants dans les gènes, les régions régulatrices et d'autres zones cliniquement pertinentes.",
      "L'analyse sépare les résultats par catégories : statut de porteur, variants monogéniques ou diagnostiques, variants de risque héréditaire comme BRCA1 ou BRCA2, marqueurs pharmacogénomiques et information de risque polygénique lorsque c'est pertinent.",
      "Ces résultats sont interprétés dans un rapport qui met en avant la génétique reproductive, les risques de santé utiles pour la planification, le métabolisme des médicaments et les points à revoir avec un conseiller en génétique ou un clinicien.",
      "Lorsqu'un fichier de données brutes est disponible, il peut être conservé avec le rapport afin que l'information puisse être réanalysée dans le temps ou connectée à l'assistant Louise pour de futures questions.",
    ],
    whyItMatters: [
      "Cela peut remplacer un dépistage de porteur plus étroit, car les résultats de portage peuvent être générés depuis le génome entier.",
      "Cela crée un dossier génétique réutilisable pour les soins de fertilité, la planification de grossesse et les décisions médicamenteuses futures.",
      "Cela peut mettre en évidence des variants cliniquement pertinents que les panels plus petits n'évaluent pas.",
      "Cela peut aider l'équipe médicale à personnaliser les décisions autour du test embryonnaire, des options de don, du métabolisme des médicaments et du conseil génétique.",
    ],
    turnaroundTime: "2 à 3 semaines",
  },
});

// ─── ALICE (variante commerciale) ────────────────────────────────────

frTest("test-chronic-endometritis-testing-products-alice", {
  title: "ALICE",
  subtitle:
    "Panel moléculaire Igenomix pour l'endométrite chronique — alternative privée, non remboursée en France",
  body: {
    testName: "ALICE (Analysis of Infectious Chronic Endometritis)",
    whatItMeasures:
      "ALICE est la version moléculaire Igenomix du test d'endométrite chronique. À partir d'une biopsie endométriale, le laboratoire recherche par séquençage l'ADN de bactéries associées à l'endométrite chronique. En France, ce n'est pas le standard : le bilan reconnu et remboursé est l'hystéroscopie + biopsie + immunomarquage CD138 sur plasmocytes. ALICE est proposé en secteur privé (~395 €, échantillon envoyé en Espagne).",
    howItWorks:
      "La biopsie est réalisée en France par un praticien puis envoyée au laboratoire Igenomix. Le rapport signale les bactéries détectées. Il ne remplace pas une évaluation clinique menée par ton équipe et ne prouve pas qu'un traitement antibiotique change les chances de naissance vivante.",
    howItWorksSteps: CHRONIC_ENDO_STEPS,
    quickFacts: BIOPSY_QUICK_FACTS,
    resultTypes: [
      {
        label: "Non détectée",
        meaning:
          "Le rapport ne détecte pas les bactéries associées à l'endométrite chronique incluses dans le panel.",
        whatToExpect:
          "C'est rassurant pour les micro-organismes testés, mais ça n'exclut ni tous les facteurs utérins ni les causes non infectieuses d'échec d'implantation.",
      },
      {
        label: "Détectée",
        meaning:
          "Le rapport détecte une ou plusieurs bactéries associées à l'endométrite chronique.",
        whatToExpect:
          "La clinique peut discuter antibiotiques ou examens complémentaires. Un organisme détecté n'est pas automatiquement actionnable sans contexte clinique.",
      },
      {
        label: "Peu concluant",
        meaning:
          "L'échantillon ou le séquençage n'est pas assez clair pour trancher.",
        whatToExpect:
          "La clinique peut proposer de refaire la biopsie ou d'utiliser un autre bilan (histologie, CD138, hystéroscopie, culture ou PCR).",
      },
    ],
    expectations: {
      canExpect: [
        "Un rapport moléculaire propre à ALICE ciblant les bactéries d'endométrite chronique",
        "Un traitement antibiotique possible si des bactéries sont détectées",
        "Un examen à interpréter dans le contexte plus large du bilan d'endométrite chronique",
      ],
      shouldNotExpect: [
        "Ne remplace pas un bilan clinique conduit par ton équipe",
        "Un organisme détecté n'est pas automatiquement actionnable sans contexte",
      ],
    },
    considerations: [
      "Demande si une endométrite chronique est réellement suspectée cliniquement (échecs d'implantation répétés, fausses couches à répétition) ou si ALICE est proposé comme add-on de routine.",
      "Demande si l'équipe confirmerait le résultat avec le bilan histologique français standard (CD138) avant de traiter.",
      "Demande précisément quel plan thérapeutique changerait si le résultat est positif.",
    ],
    bestFor: [
      "Suspicion clinique d'endométrite chronique avec suivi médical",
      "Cas sélectionnés d'échecs d'implantation répétés ou de fausses couches à répétition",
      "Non recommandé comme add-on de routine en FIV",
    ],
    keyFindings: [
      {
        direction: "negative",
        outcome:
          "Le NICE n'a pas trouvé de preuves éligibles que les tests microbiologiques comme ALICE améliorent les résultats de fertilité",
        source: "NICE NG257, revue de preuves, 2026",
      },
      {
        direction: "negative",
        outcome:
          "Un usage de routine ajoute risque de biopsie, antibiotiques, coût et délai de transfert sans bénéfice prouvé",
        source: "NICE NG257, revue de preuves, 2026",
      },
    ],
    headlineStat: "Pas de bénéfice prouvé par essai randomisé",
  },
});

// ─── EMMA (variante commerciale) ─────────────────────────────────────

frTest("test-endometrial-microbiome-testing-products-emma", {
  title: "EMMA",
  subtitle:
    "Test moléculaire Igenomix du microbiome endométrial — hors PMA standard, non remboursé",
  body: {
    testName: "EMMA (Endometrial Microbiome Metagenomic Analysis)",
    whatItMeasures:
      "EMMA est la marque Igenomix pour l'analyse du microbiome endométrial. À partir d'une biopsie, le laboratoire séquence l'ADN bactérien et rapporte le pourcentage de lactobacilles ainsi que les autres bactéries détectées. ⚠️ Hors recommandations CNGOF/HAS, non recommandé en routine par l'ESHRE 2023 : détecter des bactéries ne prouve pas que les traiter améliore les naissances vivantes. En France, non remboursé (~595 €, échantillon envoyé en Espagne).",
    howItWorks:
      "La biopsie endométriale est prélevée en France puis envoyée chez Igenomix pour séquençage. Le rapport peut suggérer antibiotiques ou probiotiques. À réserver au privé et à des indications spécifiques discutées avec l'équipe AMP — pas un examen de PMA standard.",
    quickFacts: BIOPSY_QUICK_FACTS,
    resultTypes: MICROBIOME_RESULT_TYPES,
    expectations: MICROBIOME_EXPECTATIONS,
    considerations: [
      "Ce n'est pas un examen recommandé en routine avant transfert.",
      "La biopsie ajoute inconfort, saignements, risque infectieux, coût et souvent un délai avant transfert.",
      "Un échantillon à faible biomasse peut donner un résultat bruité difficile à distinguer d'une contamination.",
      "Demande à ta clinique si c'est EMMA ou une autre marque (ERBiome) qui est prescrite, et pourquoi.",
    ],
    bestFor: MICROBIOME_BEST_FOR,
    keyFindings: MICROBIOME_KEY_FINDINGS,
    headlineStat: "Pas de bénéfice prouvé sur la naissance vivante",
  },
});

// ─── ERBiome (variante commerciale) ──────────────────────────────────

frTest("test-endometrial-microbiome-testing-products-erbiome", {
  title: "ERBiome",
  subtitle:
    "Test du microbiome endométrial CooperSurgical — hors PMA standard, non remboursé",
  body: {
    testName: "ERBiome",
    whatItMeasures:
      "ERBiome est la version CooperSurgical du test du microbiome endométrial. C'est la même catégorie qu'EMMA : à partir d'une biopsie, le laboratoire décrit la composition bactérienne de la muqueuse utérine. Peut être commandé seul ou dans le bundle ER-Complete. ⚠️ Même limite de preuve : hors recommandations CNGOF/HAS, pas de bénéfice démontré sur les naissances vivantes.",
    howItWorks:
      "Biopsie endométriale prise en France puis analysée par le laboratoire CooperSurgical. Le rapport peut mentionner antibiotiques ou probiotiques. Non remboursé.",
    quickFacts: BIOPSY_QUICK_FACTS,
    resultTypes: MICROBIOME_RESULT_TYPES,
    expectations: MICROBIOME_EXPECTATIONS,
    considerations: [
      "Demande si ERBiome est prescrit seul ou dans le bundle ER-Complete.",
      "Demande sur quelles preuves spécifiques à ERBiome ta clinique s'appuie, et pas seulement sur des associations générales sur le microbiome.",
      "Demande ce qui changerait dans ton plan si le résultat est anormal ou peu concluant.",
    ],
    bestFor: MICROBIOME_BEST_FOR,
    keyFindings: MICROBIOME_KEY_FINDINGS,
    headlineStat: "Pas de bénéfice prouvé sur la naissance vivante",
  },
});

// ─── ERA (variante commerciale) ──────────────────────────────────────

frTest("test-endometrial-receptivity-testing-products-era", {
  title: "ERA",
  subtitle:
    "Test de réceptivité endométriale Igenomix — hors PMA standard, non remboursé",
  body: {
    testName: "ERA (Endometrial Receptivity Analysis)",
    whatItMeasures:
      "ERA est la version Igenomix du test de réceptivité endométriale. À partir d'une biopsie prise dans un cycle simulé de TEC, le laboratoire mesure l'expression de gènes après une exposition définie à la progestérone et classe l'échantillon comme réceptif, pré-réceptif, post-réceptif ou peu concluant. ⚠️ Hors recommandations CNGOF/HAS, non remboursé (~650-750 €, échantillon envoyé en Espagne). Le grand essai randomisé Doyle 2022 (JAMA) n'a pas montré de bénéfice sur les naissances vivantes.",
    howItWorks:
      "ERA est réalisé dans un cycle simulé naturel ou médicamenté, pas dans le cycle de transfert réel. La biopsie est calée sur la fenêtre d'implantation attendue (souvent P+5 en cycle médicamenté ou LH/hCG+7 en cycle naturel). Igenomix analyse l'ARN endométrial. Si le rapport indique une fenêtre décalée, l'équipe peut proposer de modifier l'exposition à la progestérone ou le timing du transfert au cycle suivant.",
    howItWorksSteps: RECEPTIVITY_STEPS,
    quickFacts: BIOPSY_QUICK_FACTS,
    resultTypes: RECEPTIVITY_RESULT_TYPES,
    expectations: RECEPTIVITY_EXPECTATIONS,
    considerations: [
      "Demande pourquoi ERA est recommandé spécifiquement plutôt qu'un timing standard.",
      "Demande ce que ta clinique ferait d'un résultat pré-réceptif, post-réceptif ou peu concluant — avant de payer le test.",
      "Demande si la recommandation vient d'échecs d'implantation répétés documentés ou d'une routine de clinique.",
    ],
    bestFor: RECEPTIVITY_BEST_FOR,
    keyFindings: RECEPTIVITY_KEY_FINDINGS,
    headlineStat: NO_LIVE_BIRTH_HEADLINE,
  },
});

// ─── ERPeak (variante commerciale) ───────────────────────────────────

frTest("test-endometrial-receptivity-testing-products-erpeak", {
  title: "ERPeak",
  subtitle:
    "Test de réceptivité endométriale CooperSurgical — hors PMA standard, non remboursé",
  body: {
    testName: "ERPeak",
    whatItMeasures:
      "ERPeak est la version CooperSurgical du test de réceptivité endométriale. Comme les autres tests de la même catégorie, il utilise l'expression génique de l'endomètre pour estimer si la biopsie a été prise dans la fenêtre d'implantation prédite ou en dehors. Une étude japonaise rétrospective en RIF rapporte de meilleurs résultats, mais pas d'essai randomisé de grande taille propre à ERPeak. La limite de catégorie s'applique : pas de bénéfice de routine démontré sur les naissances vivantes.",
    howItWorks:
      "La biopsie est prise en France dans un cycle simulé, généralement autour de P+5 en cycle médicamenté ou LH/hCG+7 en cycle naturel. CooperSurgical analyse l'expression de gènes régulés par les hormones pour classer le timing. Si pré- ou post-réceptif, le transfert peut être décalé au cycle suivant.",
    howItWorksSteps: RECEPTIVITY_STEPS,
    quickFacts: BIOPSY_QUICK_FACTS,
    resultTypes: RECEPTIVITY_RESULT_TYPES,
    expectations: RECEPTIVITY_EXPECTATIONS,
    considerations: [
      "Demande sur quelles preuves spécifiques à ERPeak la clinique s'appuie — pas seulement sur la catégorie générale.",
      "Demande de combien le résultat changerait ton plan concrètement.",
      "Demande si c'est prescrit seul ou en bundle avec ERBiome ou ER-Complete.",
    ],
    bestFor: RECEPTIVITY_BEST_FOR,
    keyFindings: [
      {
        direction: "positive",
        outcome:
          "Une étude rétrospective japonaise sur des patientes en RIF a rapporté un meilleur taux de grossesse clinique et de naissance vivante après transfert personnalisé ERPeak",
        magnitude:
          "Grossesse clinique : 37,7 % vs 20,0 % ; naissance vivante : 29,9 % vs 9,7 %",
        source:
          "Ohara et al., Reproductive Medicine and Biology 2022 (rétrospective, n=550)",
      },
      {
        direction: "negative",
        outcome:
          "Les recommandations de catégorie ne soutiennent pas un usage de routine du transfert guidé par la réceptivité",
        source: "HFEA, guide add-ons ; NICE NG257",
      },
    ],
    headlineStat: "Catégorie non recommandée en routine",
  },
});

// ─── beREADY (variante commerciale) ──────────────────────────────────

frTest("test-endometrial-receptivity-testing-products-beready", {
  title: "beREADY",
  subtitle:
    "Test de réceptivité endométriale (labo estonien) — accessible via l'UE, hors PMA standard, non remboursé",
  body: {
    testName: "beREADY",
    whatItMeasures:
      "beREADY est un test de réceptivité endométriale développé par le Competence Centre on Health Technologies (Estonie). Même catégorie qu'ERA et ERPeak : biopsie endométriale suivie d'un profil d'expression génique ciblé pour estimer si la muqueuse est calée pour le transfert. Distribué avec un marquage CE-IVD dans l'UE (dont la France), mais l'accès dépend des cliniques partenaires. Pas d'essai randomisé propre à beREADY sur les naissances vivantes.",
    howItWorks:
      "La biopsie est prise dans un cycle simulé, généralement autour de P+5 en cycle médicamenté ou LH/hCG+7 en cycle naturel. L'échantillon est envoyé au laboratoire beREADY en Estonie, qui utilise un profilage d'expression ciblé (72 gènes, technologie TAC-seq). Si le rapport indique un timing décalé, l'équipe peut proposer d'ajuster le transfert.",
    howItWorksSteps: RECEPTIVITY_STEPS,
    quickFacts: BIOPSY_QUICK_FACTS,
    resultTypes: RECEPTIVITY_RESULT_TYPES,
    expectations: RECEPTIVITY_EXPECTATIONS,
    considerations: [
      "Demande quelle preuve est spécifique à beREADY versus la catégorie réceptivité en général.",
      "Demande ce qui changerait dans ton plan et de combien.",
      "Demande si une nouvelle biopsie serait nécessaire en cas de résultat peu concluant.",
    ],
    bestFor: RECEPTIVITY_BEST_FOR,
    keyFindings: [
      {
        direction: "neutral",
        outcome:
          "Les publications beREADY soutiennent le modèle d'expression génique, pas une amélioration des naissances vivantes issue du test",
        magnitude:
          "Modèle basé sur 72 gènes et profilage d'expression ciblée TAC-seq",
        source: "Meltsov et al., Scientific Reports 2023",
      },
      {
        direction: "negative",
        outcome:
          "Aucun essai randomisé propre à beREADY sur les naissances vivantes n'a été identifié",
        source: "Recommandations HFEA et NICE sur les tests de réceptivité",
      },
    ],
    headlineStat: "Catégorie non recommandée en routine",
  },
});

// ─── EndomeTRIO (variante commerciale) ───────────────────────────────

frTest("test-endometrial-add-on-bundle-products-endometrio", {
  title: "EndomeTRIO",
  subtitle:
    "Bundle Igenomix : ERA + EMMA + ALICE sur une seule biopsie — non remboursé, ~995 €",
  body: {
    testName: "EndomeTRIO (ERA + EMMA + ALICE)",
    whatItMeasures:
      "EndomeTRIO est le bundle commercial Igenomix qui combine, à partir d'une seule biopsie endométriale, le test de réceptivité (ERA), l'analyse du microbiome (EMMA) et la recherche moléculaire d'endométrite chronique (ALICE). ⚠️ Aucune des trois composantes n'entre dans les recommandations CNGOF ou HAS pour la PMA standard, et l'ESHRE 2023 déconseille l'usage en routine. Bundle non remboursé (~995 €, échantillon envoyé en Espagne).",
    howItWorks:
      "Une biopsie en cycle simulé de transfert est réalisée en France puis envoyée chez Igenomix. Le rapport peut suggérer un décalage du transfert, des antibiotiques, des probiotiques, ou une nouvelle biopsie. À réserver à des situations privées très ciblées, discutées avec l'équipe AMP — pas un examen de PMA standard.",
    quickFacts: BIOPSY_QUICK_FACTS,
    resultTypes: [
      {
        label: "Aucune composante signalée",
        meaning:
          "La réceptivité est réceptive, le microbiome n'est pas signalé et l'endométrite chronique ne détecte rien d'inquiétant.",
        whatToExpect:
          "Ça peut rassurer, mais ça ne garantit pas l'implantation ni ne prouve que le bundle améliore les naissances vivantes.",
      },
      {
        label: "ERA non-réceptif",
        meaning:
          "La partie réceptivité classe l'échantillon comme pré-réceptif ou post-réceptif.",
        whatToExpect:
          "Le rapport peut suggérer un ajustement de la progestérone ou du timing du transfert.",
      },
      {
        label: "Microbiome ou inflammation signalé",
        meaning: "La partie microbiome ou endométrite chronique est signalée.",
        whatToExpect:
          "Antibiotiques, probiotiques ou nouvelle biopsie peuvent être proposés. Demande quelle composante motive le plan.",
      },
      {
        label: "Mixte ou peu concluant",
        meaning:
          "Une ou plusieurs composantes sont floues ou incohérentes avec le reste.",
        whatToExpect:
          "Peut mener à une nouvelle biopsie ou à un délai. Demande ce qui se passerait en poursuivant un parcours standard.",
      },
    ],
    expectations: {
      canExpect: [
        "Une seule biopsie utilisée pour la réceptivité, le microbiome et l'endométrite chronique",
        "Un rapport groupé pouvant suggérer un changement de timing, antibiotiques, probiotiques ou nouvelle biopsie",
        "Un délai avant transfert",
      ],
      shouldNotExpect: [
        "Regrouper trois tests ne crée pas de preuves plus solides",
        "Les recommandations indépendantes ne préconisent pas un usage de routine",
        "Un rapport anormal ne prouve pas qu'il explique l'échec d'implantation",
      ],
    },
    considerations: [
      "Demande quelle composante du bundle motive la recommandation, et pourquoi les trois ensemble.",
      "Demande si un bilan utérin plus simple et mieux étayé a déjà été fait.",
      "Demande comment ton plan changerait si une seule composante est anormale, et si ce changement repose sur des preuves solides.",
      "Évite le bundle si la prescription est vague ou de routine.",
    ],
    bestFor: [
      "Contexte de recherche",
      "Cas très sélectionnés d'échecs d'implantation répétés après bilan standard",
      "Non recommandé comme add-on de routine en FIV",
    ],
    keyFindings: [
      {
        direction: "negative",
        outcome:
          "Le transfert calé sur la réceptivité n'a pas amélioré les naissances vivantes vs. un timing standard dans un grand essai randomisé",
        magnitude: "58,5 % vs 61,9 % (JAMA RCT)",
        source: "Doyle et al., JAMA 2022",
      },
      {
        direction: "negative",
        outcome:
          "Le NICE n'a trouvé aucune preuve d'efficacité pour EMMA ou ALICE, et pas de bénéfice significatif pour ERA",
        source: "NICE NG257, revue de preuves, 2026",
      },
    ],
    headlineStat: "Bundle non soutenu par les preuves",
  },
});

// ─── ER-Complete (variante commerciale) ──────────────────────────────

frTest("test-endometrial-add-on-bundle-products-er-complete", {
  title: "ER-Complete",
  subtitle:
    "Bundle CooperSurgical : ERPeak + ERBiome sur une seule biopsie — hors PMA standard, non remboursé",
  body: {
    testName: "ER-Complete",
    whatItMeasures:
      "ER-Complete est le bundle commercial CooperSurgical qui combine, à partir d'une seule biopsie, le test de réceptivité ERPeak et l'analyse du microbiome ERBiome. Il s'interprète à travers les mêmes limites de preuve que les deux composantes prises séparément : ni la réceptivité ni le microbiome n'ont démontré un bénéfice de routine sur les naissances vivantes.",
    howItWorks:
      "Une biopsie unique est réalisée en cycle simulé de TEC et analysée par CooperSurgical pour les deux composantes. Le rapport peut suggérer un ajustement du timing de transfert, des antibiotiques ou probiotiques.",
    quickFacts: BIOPSY_QUICK_FACTS,
    resultTypes: [
      ...RECEPTIVITY_RESULT_TYPES,
      {
        label: "Microbiome signalé",
        meaning: "La partie microbiome (ERBiome) est signalée.",
        whatToExpect:
          "Antibiotiques ou probiotiques peuvent être proposés. Demande sur quelles preuves spécifiques la clinique s'appuie.",
      },
    ],
    expectations: RECEPTIVITY_EXPECTATIONS,
    considerations: [
      "Demande quelle composante — ERPeak, ERBiome ou le bundle — motive la recommandation.",
      "Demande sur quelles preuves propres à ER-Complete la clinique s'appuie.",
      "Demande comment le plan changerait si une seule composante est anormale ou peu concluante.",
    ],
    bestFor: RECEPTIVITY_BEST_FOR,
    keyFindings: RECEPTIVITY_KEY_FINDINGS,
    headlineStat: "Bundle non soutenu par les preuves",
  },
});

// ─── Comprehensive Biomarker Panel ───────────────────────────────────

frTest("test-comprehensive-biomarker-panel", {
  title: "Bilan biomarqueurs complet",
  subtitle:
    "Plus de 100 marqueurs sanguins : fertilité, hormones, thyroïde, métabolique, inflammation, nutrition, risque grossesse",
  body: {
    testName: "Bilan biomarqueurs de fertilité complet",
    whatItMeasures:
      "Un bilan sanguin large qui va au-delà du bilan de fertilité classique. Peut inclure hormones reproductives, marqueurs thyroïdiens, marqueurs métaboliques, lipides avancés, marqueurs d'inflammation, statut nutritionnel, bilan hépatique et rénal, NFS, marqueurs auto-immuns, sérologies infectieuses, et certains marqueurs de risque de grossesse.\n\nL'objectif n'est pas d'empiler des résultats déconnectés : c'est de repérer, avant traitement ou grossesse, ce qui peut être corrigé, et de donner à ton équipe une base qui relie fertilité, santé métabolique, inflammation, nutrition et sécurité de grossesse. En France, une partie des marqueurs est prescrite et remboursée dans le bilan pré-conceptionnel ou pré-AMP ; les marqueurs plus larges (apoB, insuline à jeun, hs-CRP, vitamine D élargie) sont souvent au privé.",
    howItWorks:
      "La plupart des marqueurs se prélèvent en une seule prise de sang à jeun le matin, dans n'importe quel laboratoire. Certains marqueurs de fertilité demandent un timing propre : la progestérone se dose environ 7 jours après l'ovulation quand l'objectif est de confirmer qu'elle a eu lieu. Les résultats se lisent ensuite ensemble, en cherchant des schémas — pas marqueur par marqueur.",
    howItWorksSteps: [
      "Planifie le timing. La plupart des marqueurs se dosent à jeun le matin, mais les marqueurs cycle-dépendants (comme la progestérone) demandent leur propre timing.",
      "Fais la prise de sang principale. Le panel peut inclure hormones, thyroïde, métabolique, lipides, inflammation, nutrition, foie, reins, NFS, auto-immun, infectieux et certains marqueurs de grossesse.",
      "Ajoute les marqueurs propres au cycle ou aux antécédents si nécessaire : progestérone en mi-lutéale, anticorps antiphospholipides en cas de fausses couches à répétition, ou génétique de la thrombophilie chez certaines patientes.",
      "Lis les résultats comme un ensemble. Une lecture orientée fertilité relie insuline, TSH, vitamine D, ferritine, inflammation et prolactine au projet reproductif.",
      "Recontrôle ce qui peut évoluer : insuline à jeun, HbA1c, TSH, vitamine D, ferritine, homocystéine et prolactine peuvent être suivis après traitement ou supplémentation.",
    ],
    quickFacts: [
      { kind: "sample", value: "Sang" },
      {
        kind: "time",
        value: "Une prise principale, plus quelques marqueurs à timer",
      },
      { kind: "pain", value: "Piqûre d'aiguille" },
      { kind: "prep", value: "Prise de sang à jeun le matin pour la plupart" },
      { kind: "results", value: "Souvent 1 à 2 semaines" },
    ],
    markerTiming: [
      {
        marker: "Bilan principal",
        timing: "À jeun le matin",
        reason:
          "Le jeûne matinal rend plus interprétables les valeurs métaboliques, lipides, insuline, cortisol et certaines hormones.",
      },
      {
        marker: "Progestérone",
        timing: "Environ 7 jours après l'ovulation pour confirmer l'ovulation",
        reason:
          "La progestérone monte après l'ovulation, donc un dosage pris à un autre moment peut manquer la fenêtre utile.",
      },
      {
        marker: "Anticorps antiphospholipides",
        timing:
          "N'importe quel jour, à recontrôler ~12 semaines plus tard si positif",
        reason:
          "Un résultat positif isolé compte moins qu'un résultat persistant pour poser un diagnostic d'APS.",
      },
      {
        marker: "TSH, vitamine D, ferritine, insuline, HbA1c",
        timing: "Bilan initial, puis recontrôle après traitement",
        reason:
          "Ces marqueurs sont modifiables. Le recontrôle est calé sur la biologie de l'intervention : souvent 6 à 12 semaines, parfois environ 3 mois selon le marqueur.",
      },
    ],
    whyItMatters: [
      "L'insuline à jeun et l'HbA1c peuvent révéler une insulinorésistance ou un risque glycémique, susceptibles d'affecter l'ovulation, le SOPK, le risque de grossesse et la qualité ovocytaire.",
      "TSH, anticorps thyroïdiens, prolactine et vitamine D sont des trouvailles fréquentes et modifiables en bilan de fertilité.",
      "Ferritine, B12, folates, homocystéine, zinc, magnésium et statut oméga-3 peuvent révéler des carences nutritionnelles à corriger avant la grossesse.",
      "hs-CRP, VS, marqueurs auto-immuns et anticorps antiphospholipides ajoutent du contexte quand il y a fausses couches à répétition, échecs d'implantation, antécédents auto-immuns ou inflammation.",
      "ApoB, lipoprotéine(a), bilan LDL, fonction rénale, fonction hépatique et NFS donnent un contexte de préparation à la grossesse et de santé à long terme.",
    ],
    expectations: {
      canExpect: [
        "Un état des lieux nettement plus large qu'un bilan hormonal fertilité classique",
        "Des trouvailles modifiables à optimiser avant traitement ou grossesse",
        "Un moyen de vérifier que les interventions font bouger les marqueurs qui peuvent réellement changer",
      ],
      shouldNotExpect: [
        "Qu'un marqueur anormal soit à lui seul un diagnostic de fertilité",
        "Que tous les marqueurs se recontrôlent au même rythme",
        "Que des résultats génétiques ou d'anticorps évoluent comme la vitamine D, la TSH, la ferritine ou l'insuline",
      ],
    },
    considerations: [
      "La liste exacte des marqueurs doit être transparente — les panels commerciaux n'utilisent pas la même définition de « complet ».",
      "Tous les marqueurs ne sont pas également actionnables. La valeur vient de l'interprétation et de la priorisation, pas d'empiler des dosages.",
      "Médicaments, compléments, jeûne, timing du cycle, grossesse et infection récente peuvent modifier l'interprétation.",
      "Si un marqueur guide un traitement, demande à quand il faudra le recontrôler et quelle décision changerait selon le résultat.",
    ],
    turnaroundTime: "Souvent 1 à 2 semaines",
  },
});

// ─── Estradiol (test) ────────────────────────────────────────────────

frTest("test-estradiol", {
  title: "Prise de sang œstradiol",
  subtitle:
    "Œstradiol (E2) pour la valeur de base et le suivi de la réponse folliculaire",
  body: {
    testName: "Œstradiol (E2)",
    whatItMeasures:
      "L'œstradiol (E2) est produit par les follicules ovariens en croissance. Les valeurs de base et de stimulation s'interprètent avec l'échographie et les autres hormones, pas contre une valeur cible unique.",
    howItWorks:
      "Prise de sang dans n'importe quel labo. Souvent dosée au jour 2 à 4 du cycle pour la valeur de base, puis répétée pendant la stimulation ou la préparation au transfert. Généralement pas besoin d'être à jeun, sauf si groupée avec d'autres analyses.",
    quickFacts: [
      { kind: "sample", value: "Sang" },
      { kind: "time", value: "5 min de prélèvement" },
      { kind: "pain", value: "Piqûre d'aiguille" },
      { kind: "prep", value: "Le timing dépend du protocole" },
      { kind: "results", value: "Jour même à 2 jours ouvrés" },
    ],
    turnaroundTime: "Jour même à 2 jours ouvrés",
    cycleDay:
      "Jour 2 à 4 pour la valeur de base ; puis répété pendant la stimulation ou la préparation au transfert",
    markerTiming: [
      {
        marker: "Œstradiol (E2)",
        timing: "Jour 2 à 4 pour la base ; puis pendant la stimulation",
        reason:
          "L'œstradiol de base aide à interpréter la FSH ; les valeurs ultérieures se lisent comme une tendance avec la croissance folliculaire et le protocole.",
      },
    ],
  },
});

// ─── LH (test) ────────────────────────────────────────────────────────

frTest("test-lh", {
  title: "Prise de sang LH",
  subtitle:
    "Hormone lutéinisante pour le timing de l'ovulation et la valeur de base",
  body: {
    testName: "Hormone lutéinisante (LH)",
    whatItMeasures:
      "L'hormone lutéinisante (LH) coordonne l'ovulation. Une valeur de base s'interprète avec la FSH, l'œstradiol et l'AMH ; une valeur en milieu de cycle peut repérer un pic ovulatoire.",
    howItWorks:
      "Prise de sang dans n'importe quel labo. Le timing dépend de la question : jour 2 à 4 pour la base, ou milieu de cycle si on cherche un pic ovulatoire. Généralement pas besoin d'être à jeun, sauf si groupée avec d'autres analyses.",
    quickFacts: [
      { kind: "sample", value: "Sang" },
      { kind: "time", value: "5 min de prélèvement" },
      { kind: "pain", value: "Piqûre d'aiguille" },
      { kind: "prep", value: "Le timing dépend de la question posée" },
      { kind: "results", value: "1 à 2 jours ouvrés" },
    ],
    turnaroundTime: "1 à 2 jours ouvrés",
    cycleDay:
      "Jour 2 à 4 pour la base ; milieu de cycle pour rechercher un pic",
    markerTiming: [
      {
        marker: "LH",
        timing: "Jour 2 à 4 pour la base ; milieu de cycle pour un pic",
        reason:
          "La même hormone répond à des questions différentes selon quand elle est mesurée, donc le timing va avec le résultat.",
      },
    ],
  },
});

// ─── Datapoints (marqueurs affichés dans les cartes) ─────────────────

frDataPoint("dp-estradiol-level", {
  title: "Œstradiol",
  body: {
    normalRange:
      "Dépend de la phase du cycle, du protocole et de la réponse folliculaire",
    interpretation:
      "L'œstradiol se lit comme une tendance, avec la croissance folliculaire et le timing du protocole. Pendant la stimulation, chaque follicule mature contribue à peu près 200 à 300 pg/mL, mais les cliniques regardent l'ensemble du profil plutôt qu'une seule valeur.",
  },
});

frDataPoint("dp-lh-level", {
  title: "LH",
  body: {
    normalRange: "Dépend de la phase du cycle et du protocole",
    interpretation:
      "La LH s'interprète surtout autour du timing de l'ovulation, de la suppression ou de la planification du déclenchement.",
  },
});

frDataPoint("dp-fsh-level", {
  title: "FSH",
  body: {
    normalRange:
      "S'interprète en général sur une prise de sang de début de cycle",
    interpretation:
      "La FSH de base se lit avec l'œstradiol, l'AMH, le CFA, l'âge et le contexte thérapeutique. Une FSH qui monte au jour 3 sur plusieurs cycles peut être un signal pour ne pas trop attendre, surtout si la réserve est déjà basse.",
  },
});

frDataPoint("dp-progesterone-level", {
  title: "Progestérone",
  body: {
    normalRange:
      "Folliculaire 0,1-1,5 / Ovulation 1-3 / Mi-lutéale (J+7 post-ovulation) 5-20 / Ménopause <0,5 ng/mL",
    interpretation:
      "La progestérone est très sensible au timing : elle s'interprète en fonction du moment de l'ovulation ou du calendrier de médicaments. Un dosage en mi-lutéale (~7 jours après l'ovulation) avec un résultat >3 ng/mL confirme qu'il y a eu ovulation. La progestérone est pulsatile (elle oscille entre 5 et 40 ng/mL en quelques heures) — une valeur isolée ne suffit pas à diagnostiquer une insuffisance lutéale (ASRM 2021). En TEC médicamenté, un seuil d'environ 10 ng/mL est fréquemment utilisé.",
  },
});

frDataPoint("dp-tsh-level", {
  title: "TSH",
  body: {
    normalRange:
      "Les cliniques de fertilité visent souvent une cible pré-conceptionnelle plus stricte, communément inférieure à 2,5 mIU/L en projet de grossesse",
    interpretation:
      "La TSH s'interprète avec les antécédents thyroïdiens, les symptômes, la T4 libre, les médicaments et le projet de grossesse. Si elle est élevée, un traitement fait généralement bouger la valeur en 6 à 8 semaines.",
  },
});

frDataPoint("dp-free-t4-level", {
  title: "T4 libre",
  body: {
    normalRange: "Les valeurs de référence varient selon le laboratoire",
    interpretation:
      "La T4 libre aide à clarifier le statut thyroïdien quand la TSH est anormale, ou pour suivre un traitement en cours.",
  },
});

frDataPoint("dp-free-t3-level", {
  title: "T3 libre",
  body: {
    normalRange: "Les valeurs de référence varient selon le laboratoire",
    interpretation:
      "La T3 libre est parfois ajoutée quand l'équipe soignante veut un tableau thyroïdien plus complet.",
  },
});

frDataPoint("dp-vitamin-d-level", {
  title: "Vitamine D",
  body: {
    normalRange:
      "Les cibles varient selon la clinique et les antécédents ; certains programmes de fertilité visent au-dessus de 40 ng/mL",
    interpretation:
      "La vitamine D se lit avec la supplémentation, l'alimentation, l'exposition solaire et le contexte général. Une carence est fréquente et souvent corrigeable en ~8 semaines de supplémentation.",
  },
});

frDataPoint("dp-beta-hcg-level", {
  title: "Béta-hCG",
  body: {
    normalRange:
      "Les seuils de positivité dépendent du laboratoire et du timing",
    interpretation:
      "La béta-hCG s'interprète par le timing (jours après l'ovulation ou le transfert) et par la tendance sur des dosages répétés — pas par une seule valeur isolée.",
  },
});

frDataPoint("dp-prolactin-level", {
  title: "Prolactine",
  body: {
    normalRange:
      "Les valeurs de référence varient selon le labo et le statut grossesse/allaitement",
    interpretation:
      "La prolactine se lit avec les symptômes, la régularité du cycle, le stress, les médicaments et un dosage répété au besoin.",
  },
});

frDataPoint("dp-testosterone-level", {
  title: "Testostérone",
  body: {
    normalRange:
      "Les valeurs de référence varient selon le sexe, l'âge et le labo",
    interpretation:
      "La testostérone s'interprète avec les symptômes, le profil du cycle, les autres marqueurs androgéniques et le contexte (SOPK notamment).",
  },
});

frDataPoint("dp-dhea-s-level", {
  title: "DHEA-S",
  body: {
    normalRange: "Les valeurs de référence varient selon l'âge et le labo",
    interpretation:
      "La DHEA-S se lit avec les signes d'hyperandrogénie, le contexte surrénalien, les marqueurs de réserve et l'usage éventuel de compléments.",
  },
});

frDataPoint("dp-fasting-insulin", {
  title: "Insuline à jeun",
  body: {
    normalRange:
      "Les valeurs de référence varient selon le labo et le contexte métabolique",
    interpretation:
      "L'insuline à jeun dépiste l'insulinorésistance, fréquente dans le SOPK et pouvant affecter l'ovulation et la qualité ovocytaire. Elle peut s'améliorer en 2 à 3 mois avec un changement alimentaire, de l'activité physique, l'inositol, la metformine ou une prise en charge médicale.",
  },
});

frDataPoint("dp-hba1c", {
  title: "HbA1c",
  body: {
    normalRange:
      "En dessous de 5,7 % est généralement considéré comme non-diabétique",
    interpretation:
      "L'HbA1c reflète la moyenne de la glycémie sur environ 3 mois. Utile en pré-conceptionnel car l'équilibre glycémique influence le risque de grossesse, et elle peut évoluer sur la même fenêtre de temps grâce aux ajustements métaboliques.",
  },
});

frDataPoint("dp-hs-crp", {
  title: "hs-CRP",
  body: {
    normalRange:
      "Les valeurs de référence varient selon le labo et le contexte cardiovasculaire",
    interpretation:
      "La hs-CRP est un marqueur d'inflammation systémique. En fertilité, elle se lit avec le contexte infectieux, auto-immun, métabolique et cardiovasculaire — pas comme une réponse isolée sur l'implantation.",
  },
});

frDataPoint("dp-homocysteine", {
  title: "Homocystéine",
  body: {
    normalRange: "Les valeurs de référence varient selon le laboratoire",
    interpretation:
      "L'homocystéine s'interprète avec les folates, la B12, la B6, la thyroïde, la fonction rénale et le contexte cardiovasculaire. Quand elle est élevée, elle peut baisser avec du méthylfolate, de la B12, de la B6 et des ajustements nutritionnels sur environ 8 semaines.",
  },
});

frDataPoint("dp-ferritin", {
  title: "Ferritine",
  body: {
    normalRange:
      "Les valeurs de référence varient selon le labo ; certains programmes de fertilité visent au-dessus de 40 ng/mL",
    interpretation:
      "La ferritine reflète les réserves en fer. Elle se lit avec la NFS, l'inflammation, les antécédents de saignements, l'alimentation et le projet de grossesse. Reconstituer des réserves basses prend souvent 3 à 6 mois de supplémentation.",
  },
});

frDataPoint("dp-apob", {
  title: "ApoB",
  body: {
    normalRange: "Les cibles dépendent du risque cardiovasculaire",
    interpretation:
      "L'ApoB estime le nombre de particules de cholestérol athérogènes ; elle est souvent plus informative que le LDL seul. Un bon repère pré-conceptionnel car la grossesse augmente la charge cardiovasculaire.",
  },
});

frDataPoint("dp-antiphospholipid-antibodies", {
  title: "Anticorps antiphospholipides",
  body: {
    normalRange: "Négatif",
    interpretation:
      "La recherche d'anticorps antiphospholipides peut inclure les anticorps anticardiolipine, l'anticoagulant lupique et les anti-bêta-2 glycoprotéine. Un résultat positif doit être confirmé par un contrôle environ 12 semaines plus tard avant de retenir un diagnostic de syndrome des antiphospholipides.",
  },
});

frDataPoint("dp-thrombophilia-genetics", {
  title: "Génétique de la thrombophilie",
  body: {
    normalRange: "Aucun variant cliniquement pertinent détecté",
    interpretation:
      "Les résultats de thrombophilie héréditaire (facteur V Leiden, mutation du gène de la prothrombine, etc.) ne changent pas dans le temps, mais ils peuvent influencer les décisions d'anticoagulation en grossesse ou en FIV pour certaines patientes.",
  },
});

frDataPoint("dp-follicle-count", {
  title: "Nombre de follicules",
  body: {
    normalRange:
      "Dépend de l'âge, de la réserve, de la phase du cycle et du protocole",
    interpretation:
      "Le nombre de follicules s'interprète différemment en base et en cours de stimulation. Le CFA de base peut varier d'environ 2 à 5 follicules d'un cycle à l'autre ; en stimulation, on suit le nombre de follicules qui poussent activement.",
  },
});

frDataPoint("dp-follicle-size", {
  title: "Taille des follicules",
  body: {
    normalRange:
      "La taille attendue dépend du jour de stimulation et du protocole",
    interpretation:
      "Les tailles folliculaires aident la clinique à comparer la cohorte et à caler le déclenchement. En stimulation, les follicules poussent souvent d'environ 1 à 2 mm par jour, et les follicules leaders autour de 18 à 20 mm sont souvent considérés prêts au déclenchement selon le protocole.",
  },
});

frDataPoint("dp-endometrial-thickness", {
  title: "Endomètre",
  body: {
    normalRange: "Dépend de la phase du cycle et du protocole de transfert",
    interpretation:
      "L'épaisseur de l'endomètre s'interprète avec l'aspect (trilaminaire ou non), le protocole, les saignements et le timing du transfert. Beaucoup de cliniques visent au moins 7 à 8 mm avec un aspect trilaminaire avant de planifier un transfert.",
  },
});

frDataPoint("dp-sperm-dna-fragmentation", {
  title: "Fragmentation de l'ADN spermatique",
  body: {
    normalRange:
      "Les valeurs de référence dépendent de la méthode utilisée par le labo",
    interpretation:
      "La fragmentation de l'ADN se lit avec le spermogramme, les antécédents et les résultats de fertilité obtenus jusque-là.",
  },
});
